import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Autocomplete,
} from '@mui/material';
import {
  RiAspectRatioLine,
  RiArrowLeftRightLine,
  RiArrowUpDownLine,
  RiEyeLine,
  RiEyeOffLine,
  RiDeleteBinLine,
  RiExpandHorizontalLine,
  RiExpandHorizontalSFill,
  RiExpandVerticalSFill,
  RiSubtractLine,
  RiVideoLine,
  RiAlignLeft,
  RiAlignCenter,
  RiAlignRight,
  RiAlignTop,
  RiAlignBottom,
  RiRotateLockLine,
  RiFlipVerticalLine,
  RiFlipHorizontalLine,
  RiFullscreenLine,
  RiAddLine,
  RiCloseLine,
  RiAlignItemLeftFill,
  RiAlignItemHorizontalCenterFill,
  RiAlignItemRightFill,
  RiAlignItemTopFill,
  RiAlignItemVerticalCenterFill,
  RiAlignItemBottomFill,
  RiAspectRatioFill,
  RiClockwiseLine,
  RiFlipVertical2Line,
  RiFlipHorizontal2Line,
  RiContrastLine,
  RiRoundedCorner,
  RiEditLine,
  RiEdit2Fill,
  RiListCheck2,
} from '@remixicon/react';
import BackgroundModal from './BackgroundModal';
import ColorPickerModal from './ColorPickerModal';
import Separator from './Separator';
import AnimationModal from './AnimationModal';
import MenuItemsModal from './MenuItemsModal';
import { FONT_FAMILIES } from './constants';
import {
  updateElementProperties,
  updateElementPosition,
  updateElementSize,
  setOrientation,
  updateCanvasDimensions,
  setBackground,
  saveToHistory,
  addAnimationToElement,
  removeAnimationFromElement,
  addElement,
} from '../../redux/slices/canvasSlice';

/**
 * Properties Panel V2 - Shows canvas properties by default, element properties when selected
 */
const PropertiesPanelV2 = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { takeawayList } = useSelector((state) => state.auth || {});
  const { elements, selectedElementId, orientation, dimensions, background } = useSelector(
    (state) => state.canvas || {}
  );

  const selectedElement = elements?.find((el) => el.id === selectedElementId);
  const canvasDimensions = dimensions?.[orientation] || { width: 1920, height: 1080 };

  // Determine selected branch from location state and takeawayList
  const { branchId } = location.state || {};
  const selectedBranch =
    branchId && takeawayList && takeawayList.length > 0
      ? takeawayList.find((b) => (b.store_id || b.id) === branchId)
      : null;

  const branchLogoUrl =
    selectedBranch?.website_logo_url ||
    selectedBranch?.website_logo ||
    selectedBranch?.logo_url ||
    selectedBranch?.logo ||
    null;

  // Dedicated logo element (marked by properties.isLogo)
  const logoElement = elements?.find((el) => el.properties?.isLogo);

  // Local state for canvas dimensions (only used when no element selected)
  const [width, setWidth] = useState(canvasDimensions.width);
  const [height, setHeight] = useState(canvasDimensions.height);
  const [backgroundModalOpen, setBackgroundModalOpen] = useState(false);

  // Color picker modals for shapes
  const [fillColorModalOpen, setFillColorModalOpen] = useState(false);
  const [strokeColorModalOpen, setStrokeColorModalOpen] = useState(false);
  const [boxShadowColorModalOpen, setBoxShadowColorModalOpen] = useState(false);

  // Animation modal state (for element animations)
  const [animationModalOpen, setAnimationModalOpen] = useState(false);

  // Color picker modals for text
  const [textColorModalOpen, setTextColorModalOpen] = useState(false);
  const [textFillColorModalOpen, setTextFillColorModalOpen] = useState(false);
  const [textBorderColorModalOpen, setTextBorderColorModalOpen] = useState(false);

  // Menu item tab state
  const [menuItemTab, setMenuItemTab] = useState(0);
  const [menuItemPriceColorModalOpen, setMenuItemPriceColorModalOpen] = useState(false);
  const [menuItemDescriptionColorModalOpen, setMenuItemDescriptionColorModalOpen] = useState(false);
  const [menuItemsModalOpen, setMenuItemsModalOpen] = useState(false);

  // Logo helpers
  const logoPosition = logoElement?.position || { x: 40, y: 40 };
  const logoSize = logoElement?.size || { width: 200, height: 200 };
  const logoProperties = logoElement?.properties || {};

  const handleAddLogoFromBranch = () => {
    if (!branchLogoUrl) return;

    dispatch(saveToHistory());

    // If a logo element already exists, just update its image
    if (logoElement) {
      dispatch(
        updateElementProperties({
          id: logoElement.id,
          properties: {
            ...logoProperties,
            src: branchLogoUrl,
            alt: selectedBranch?.name || selectedBranch?.store_name || 'Logo',
            isLogo: true,
            objectFit: 'contain',
          },
        })
      );
      return;
    }

    // Otherwise, add a new image element marked as logo
    dispatch(
      addElement({
        type: 'image',
        position: logoPosition,
        size: logoSize,
        properties: {
          src: branchLogoUrl,
          alt: selectedBranch?.name || selectedBranch?.store_name || 'Logo',
          isLogo: true,
          objectFit: 'contain',
        },
      })
    );
  };

  const handleLogoPositionChange = (axis, value) => {
    if (!logoElement) return;
    const numeric = parseFloat(value) || 0;
    dispatch(
      updateElementPosition({
        id: logoElement.id,
        position: {
          ...logoPosition,
          [axis]: numeric,
        },
      })
    );
  };

  const handleLogoSizeChange = (dimensionKey, value) => {
    if (!logoElement) return;
    const numeric = parseFloat(value) || 0;
    dispatch(
      updateElementSize({
        id: logoElement.id,
        size: {
          ...logoSize,
          [dimensionKey]: numeric,
        },
      })
    );
  };

  const handleLogoPropertyChange = (key, value) => {
    if (!logoElement) return;
    dispatch(
      updateElementProperties({
        id: logoElement.id,
        properties: {
          ...logoProperties,
          [key]: value,
        },
      })
    );
  };

  useEffect(() => {
    setWidth(canvasDimensions.width);
    setHeight(canvasDimensions.height);
  }, [canvasDimensions]);

  // Reset menu item tab when element changes
  useEffect(() => {
    if (selectedElement?.type !== 'menuItem') {
      setMenuItemTab(0);
    }
  }, [selectedElementId, selectedElement?.type]);

  const animations = selectedElement?.animations || [];
  const primaryAnimation = animations.length > 0 ? animations[0] : null;

  const handleOpenAnimationModal = () => {
    setAnimationModalOpen(true);
  };

  const handleCloseAnimationModal = () => {
    setAnimationModalOpen(false);
  };

  const handleSaveAnimation = (config) => {
    if (!selectedElementId) return;

    if (primaryAnimation) {
      dispatch(
        removeAnimationFromElement({
          elementId: selectedElementId,
          animationId: primaryAnimation.id,
        })
      );
    }

    dispatch(
      addAnimationToElement({
        elementId: selectedElementId,
        animation: config,
      })
    );
  };

  const handleRemovePrimaryAnimation = () => {
    if (!selectedElementId || !primaryAnimation) return;
    dispatch(
      removeAnimationFromElement({
        elementId: selectedElementId,
        animationId: primaryAnimation.id,
      })
    );
  };

  // Canvas Properties (shown by default)
  if (!selectedElement) {

    const handleWidthChange = (value) => {
      const newWidth = parseInt(value) || 0;
      setWidth(newWidth);
      dispatch(updateCanvasDimensions({ width: newWidth }));
    };

    const handleHeightChange = (value) => {
      const newHeight = parseInt(value) || 0;
      setHeight(newHeight);
      dispatch(updateCanvasDimensions({ height: newHeight }));
    };

    const handleAspectRatio = () => {
      dispatch(updateCanvasDimensions({ height: width }));
      setHeight(width);
    };

    const handleLandscape = () => {
      dispatch(saveToHistory());
      dispatch(setOrientation('landscape'));
      dispatch(updateCanvasDimensions({ width: 1920, height: 1080 }));
      setWidth(1920);
      setHeight(1080);
    };

    const handlePortrait = () => {
      dispatch(saveToHistory());
      dispatch(setOrientation('portrait'));
      dispatch(updateCanvasDimensions({ width: 1080, height: 1920 }));
      setWidth(1080);
      setHeight(1920);
    };

    // Background color state
    const bgColor = background.type === 'color' ? background.value : '#FFFFFF';
    const bgOpacity = background.opacity !== undefined ? background.opacity : 100;
    const isImageOrVideo = background.type === 'image' || background.type === 'video';

    // Helper function to convert hex to rgba
    const hexToRgba = (hex, opacity) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
    };

    // Helper function to extract hex from rgba or return hex
    const getHexFromColor = (color) => {
      if (color.startsWith('rgba')) {
        const matches = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
        if (matches) {
          const r = parseInt(matches[1]).toString(16).padStart(2, '0');
          const g = parseInt(matches[2]).toString(16).padStart(2, '0');
          const b = parseInt(matches[3]).toString(16).padStart(2, '0');
          return `#${r}${g}${b}`;
        }
      }
      return color;
    };

    const handleBackgroundColorChange = (value) => {
      dispatch(saveToHistory());
      // If we have opacity, convert to rgba
      if (bgOpacity < 100) {
        const rgbaColor = hexToRgba(value, bgOpacity);
        dispatch(setBackground({ type: 'color', value: rgbaColor, opacity: bgOpacity }));
      } else {
        dispatch(setBackground({ type: 'color', value, opacity: bgOpacity }));
      }
    };

    const handleBackgroundOpacityChange = (value) => {
      dispatch(saveToHistory());
      const opacity = Math.max(0, Math.min(100, parseInt(value) || 100));
      const hexColor = getHexFromColor(bgColor);

      if (opacity < 100) {
        const rgbaColor = hexToRgba(hexColor, opacity);
        dispatch(setBackground({ type: 'color', value: rgbaColor, opacity }));
      } else {
        dispatch(setBackground({ type: 'color', value: hexColor, opacity: 100 }));
      }
    };

    const handleToggleBackgroundVisibility = () => {
      dispatch(saveToHistory());
      if (background.type === 'color' && background.value === 'transparent') {
        dispatch(setBackground({ type: 'color', value: '#FFFFFF', opacity: bgOpacity }));
      } else {
        dispatch(setBackground({ type: 'color', value: 'transparent', opacity: bgOpacity }));
      }
    };

    const handleRemoveBackground = () => {
      dispatch(saveToHistory());
      dispatch(setBackground({ type: 'color', value: '#FFFFFF', opacity: 100 }));
    };

    const isBackgroundVisible = background.type === 'color'
      ? background.value !== 'transparent'
      : true;
    console.log('menuItemTab', menuItemTab);
    return (
      <Box sx={{ padding: '16px' }}>
        {/* Canvas Section */}
        <Box sx={{ marginBottom: '24px' }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: '16px',
              color: '#000000',
              marginBottom: '12px',
            }}
          >
            Canvas
          </Typography>

          {/* Width & Height Inputs */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              marginBottom: '12px',
            }}
          >
            <Box sx={{ position: 'relative', flex: 1 }}>
              <Typography
                sx={{
                  position: 'absolute',
                  left: '12px',
                  top: '8px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#000000',
                  zIndex: 1,
                }}
              >
                W
              </Typography>
              <TextField
                type="number"
                value={width}
                onChange={(e) => handleWidthChange(e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F5F5F5',
                    borderRadius: '8px',
                    paddingLeft: '32px',
                    '& fieldset': {
                      border: 'none',
                    },
                    '& input': {
                      padding: '8px 12px',
                      fontSize: '14px',
                      fontWeight: 400,
                    },
                  },
                }}
                fullWidth
              />
            </Box>

            <Box sx={{ position: 'relative', flex: 1 }}>
              <Typography
                sx={{
                  position: 'absolute',
                  left: '12px',
                  top: '8px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#000000',
                  zIndex: 1,
                }}
              >
                H
              </Typography>
              <TextField
                type="number"
                value={height}
                onChange={(e) => handleHeightChange(e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F5F5F5',
                    borderRadius: '8px',
                    paddingLeft: '32px',
                    '& fieldset': {
                      border: 'none',
                    },
                    '& input': {
                      padding: '8px 12px',
                      fontSize: '14px',
                      fontWeight: 400,
                    },
                  },
                }}
                fullWidth
              />
            </Box>

            {/* Aspect Ratio Lock */}
            <IconButton
              onClick={handleAspectRatio}
              sx={{
                backgroundColor: '#F5F5F5',
                borderRadius: '8px',
                width: '40px',
                height: '40px',
                border: 'none',
                '&:hover': {
                  backgroundColor: '#E0E0E0',
                },
              }}
            >
              <RiAspectRatioLine size={20} color="#000000" />
            </IconButton>
          </Box>

          {/* Landscape/Portrait Buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={handleLandscape}
              variant={orientation === 'landscape' ? 'contained' : 'outlined'}
              startIcon={<RiExpandHorizontalSFill size={24} />}
              sx={{
                flex: 1,
                textTransform: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: 500,
                ...(orientation === 'landscape'
                  ? {
                    backgroundColor: '#000000',
                    color: '#FFFFFF',
                    '&:hover': {
                      backgroundColor: '#333333',
                    },
                  }
                  : {
                    backgroundColor: '#FFFFFF',
                    color: '#000000',
                    border: '1px solid #E0E0E0',
                    '&:hover': {
                      backgroundColor: '#F5F5F5',
                      border: '1px solid #E0E0E0',
                    },
                  }),
              }}
            >
              Landscape
            </Button>
            <Button
              onClick={handlePortrait}
              variant={orientation === 'portrait' ? 'contained' : 'outlined'}
              startIcon={<RiExpandVerticalSFill size={24} />}
              sx={{
                flex: 1,
                textTransform: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: 500,
                ...(orientation === 'portrait'
                  ? {
                    backgroundColor: '#000000',
                    color: '#FFFFFF',
                    '&:hover': {
                      backgroundColor: '#333333',
                    },
                  }
                  : {
                    backgroundColor: '#FFFFFF',
                    color: '#000000',
                    border: '1px solid #E0E0E0',
                    '&:hover': {
                      backgroundColor: '#F5F5F5',
                      border: '1px solid #E0E0E0',
                    },
                  }),
              }}
            >
              Portrait
            </Button>
          </Box>

          {/* Logo from Branch */}
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {branchLogoUrl && (
                <Box
                  component="img"
                  src={branchLogoUrl}
                  alt={selectedBranch?.name || selectedBranch?.store_name || 'Logo'}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '6px',
                    objectFit: 'contain',
                    border: '1px solid #E0E0E0',
                    backgroundColor: '#FFFFFF',
                  }}
                />
              )}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: '13px',
                  color: '#000000',
                }}
              >
                Branch logo
              </Typography>
            </Box>

            <Button
              variant="outlined"
              size="small"
              disabled={!branchLogoUrl}
              onClick={handleAddLogoFromBranch}
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 500,
              }}
            >
              {logoElement ? 'Update logo' : 'Add logo'}
            </Button>
          </Box>
        </Box>

        {/* Separator */}
        <Separator withAccent={false} />

        {/* Background Color Section - Single Container */}
        <Box
          sx={{
            backgroundColor: '#F5F5F5',
            borderRadius: '8px',
            padding: '8px',
            display: 'flex',
            gap: 1,
            alignItems: 'center',
          }}
        >
          {/* Color/Image/Video Swatch - Clickable */}
          <Box
            onClick={() => setBackgroundModalOpen(true)}
            sx={{
              width: '40px',
              height: '40px',
              borderRadius: '6px',
              backgroundColor: isImageOrVideo ? '#E3F2FD' : bgColor === 'transparent' ? '#FFFFFF' : getHexFromColor(bgColor),
              border: '1px solid #E0E0E0',
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
              flexShrink: 0,
              '&:hover': {
                borderColor: '#999',
              },
            }}
          >
            {background.type === 'image' && (
              <Box
                component="img"
                src={background.value}
                alt="Background"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            )}
            {background.type === 'video' && (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#E3F2FD',
                }}
              >
                <RiVideoLine size={20} color="#1976D2" />
              </Box>
            )}
            {background.type === 'color' && bgColor === 'transparent' && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage:
                    'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
                  backgroundSize: '8px 8px',
                  backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
                }}
              />
            )}
          </Box>

          {/* Color Code Input or Background Type Display */}
          <TextField
            type="text"
            value={
              isImageOrVideo
                ? background.type === 'image'
                  ? 'Image'
                  : 'Video'
                : bgColor === 'transparent'
                  ? 'FFFFFF'
                  : getHexFromColor(bgColor).replace('#', '').toUpperCase()
            }
            onChange={(e) => {
              if (!isImageOrVideo) {
                const value = e.target.value.replace('#', '');
                if (value.length === 6) {
                  handleBackgroundColorChange(`#${value}`);
                }
              }
            }}
            disabled={isImageOrVideo}
            size="small"
            sx={{
              flex: 1,
              minWidth: 0,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'transparent',
                borderRadius: '6px',
                '& fieldset': {
                  border: 'none',
                },
                '& input': {
                  padding: '4px 8px',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#000000',
                  textTransform: isImageOrVideo ? 'none' : 'uppercase',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'transparent',
                  '& input': {
                    color: '#000000',
                  },
                },
              },
            }}
          />

          {/* Opacity Input with % */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TextField
              type="number"
              value={bgOpacity}
              onChange={(e) => handleBackgroundOpacityChange(e.target.value)}
              size="small"
              sx={{
                width: '50px',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'transparent',
                  borderRadius: '6px',
                  '& fieldset': {
                    border: 'none',
                  },
                  '& input': {
                    padding: '4px 4px',
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#000000',
                    textAlign: 'right',
                  },
                },
              }}
            />
            <Typography sx={{ fontSize: '14px', color: '#666666', marginRight: '4px' }}>
              %
            </Typography>
          </Box>

          {/* Visibility Toggle */}
          <IconButton
            onClick={handleToggleBackgroundVisibility}
            size="small"
            sx={{
              padding: '4px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.05)',
              },
            }}
          >
            {isBackgroundVisible ? (
              <RiEyeLine size={20} />
            ) : (
              <RiEyeOffLine size={20} />
            )}
          </IconButton>

          {/* Remove Button */}
          <IconButton
            onClick={handleRemoveBackground}
            size="small"
            sx={{
              padding: '4px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.05)',
              },
            }}
          >
            <RiSubtractLine size={20} />
          </IconButton>
        </Box>

        {/* Background Modal */}
        <BackgroundModal
          open={backgroundModalOpen}
          onClose={() => setBackgroundModalOpen(false)}
        />
      </Box>
    );
  }

  // Element Properties (shown when element is selected)
  const { properties, position, size, type } = selectedElement;

  const handlePropertyChange = (key, value) => {
    dispatch(saveToHistory());
    dispatch(
      updateElementProperties({
        id: selectedElementId,
        properties: { [key]: value },
      })
    );
  };

  const handlePositionChange = (axis, value) => {
    dispatch(
      updateElementPosition({
        id: selectedElementId,
        position: { ...position, [axis]: parseFloat(value) || 0 },
      })
    );
  };

  const handleSizeChange = (dimension, value) => {
    dispatch(
      updateElementSize({
        id: selectedElementId,
        size: { ...size, [dimension]: parseFloat(value) || 0 },
      })
    );
  };

  return (
    <Box sx={{ padding: '16px' }}>
      {/* Element Type Header */}
      <Box sx={{ marginBottom: '16px' }}>
        <Typography variant="h6" fontWeight={600}>
          {type.toUpperCase()}
        </Typography>
      </Box>

      {/* Animation Section (shared for any element type) */}
      <Box sx={{ marginBottom: '24px' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.5,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, fontSize: '14px', color: '#000000' }}
          >
            Animation
          </Typography>
          <IconButton
            size="small"
            onClick={handleOpenAnimationModal}
            sx={{
              borderRadius: '50%',
              border: '1px solid #E5E7EB',
              width: 28,
              height: 28,
            }}
          >
            <RiAddLine size={18} />
          </IconButton>
        </Box>

        {!primaryAnimation && (
          <Typography variant="body2" color="text.secondary">
            No animation selected
          </Typography>
        )}

        {primaryAnimation && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#F5F5F8',
              borderRadius: 2,
              px: 1.5,
              py: 1,
              mt: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1.5,
                  backgroundColor: '#EEE5FF',
                }}
              />
              <Box>
                <Typography variant="body2">
                  {primaryAnimation.type || 'Animation'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {primaryAnimation.duration || 1000} ms
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Button
                size="small"
                variant="text"
                onClick={handleOpenAnimationModal}
                sx={{ textTransform: 'none', fontSize: 12 }}
              >
                Edit
              </Button>
              <IconButton
                size="small"
                onClick={handleRemovePrimaryAnimation}
                sx={{ ml: 0.5 }}
              >
                <RiDeleteBinLine size={16} />
              </IconButton>
            </Box>
          </Box>
        )}

        <AnimationModal
          open={animationModalOpen}
          onClose={handleCloseAnimationModal}
          initialAnimation={primaryAnimation}
          onSave={handleSaveAnimation}
        />
      </Box>

      {/* Position & Size */}
      {/* <Box sx={{ marginBottom: '24px' }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            fontSize: '14px',
            color: '#000000',
            marginBottom: '12px',
          }}
        >
          Position
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
          <TextField
            label="X"
            type="number"
            size="small"
            value={Math.round(position.x)}
            onChange={(e) => {
              dispatch(saveToHistory());
              handlePositionChange('x', e.target.value);
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#F5F5F5',
                borderRadius: '6px',
                '& fieldset': { border: 'none' },
              },
            }}
          />
          <TextField
            label="Y"
            type="number"
            size="small"
            value={Math.round(position.y)}
            onChange={(e) => {
              dispatch(saveToHistory());
              handlePositionChange('y', e.target.value);
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#F5F5F5',
                borderRadius: '6px',
                '& fieldset': { border: 'none' },
              },
            }}
          />
        </Box>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            fontSize: '14px',
            color: '#000000',
            marginBottom: '12px',
          }}
        >
          Size
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          <TextField
            label="Width"
            type="number"
            size="small"
            value={Math.round(size.width)}
            onChange={(e) => {
              dispatch(saveToHistory());
              handleSizeChange('width', e.target.value);
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#F5F5F5',
                borderRadius: '6px',
                '& fieldset': { border: 'none' },
              },
            }}
          />
          <TextField
            label="Height"
            type="number"
            size="small"
            value={Math.round(size.height)}
            onChange={(e) => {
              dispatch(saveToHistory());
              handleSizeChange('height', e.target.value);
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#F5F5F5',
                borderRadius: '6px',
                '& fieldset': { border: 'none' },
              },
            }}
          />
        </Box>
      </Box> */}

      {/* Logo Properties (position / size / rotation & flip) */}
      {logoElement && (
        <Box sx={{ marginBottom: '24px' }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              fontSize: '14px',
              color: '#000000',
              marginBottom: '12px',
            }}
          >
            Logo Position & Size
          </Typography>

          {/* Position */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              marginBottom: '12px',
            }}
          >
            <Box sx={{ position: 'relative', flex: 1 }}>
              <Typography
                sx={{
                  position: 'absolute',
                  left: '12px',
                  top: '8px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#000000',
                  zIndex: 1,
                }}
              >
                X
              </Typography>
              <TextField
                type="number"
                value={Math.round(logoPosition.x)}
                onChange={(e) => {
                  dispatch(saveToHistory());
                  handleLogoPositionChange('x', e.target.value);
                }}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F5F5F5',
                    borderRadius: '8px',
                    paddingLeft: '32px',
                    '& fieldset': { border: 'none' },
                    '& input': { padding: '8px 12px', fontSize: '14px' },
                  },
                }}
                fullWidth
              />
            </Box>
            <Box sx={{ position: 'relative', flex: 1 }}>
              <Typography
                sx={{
                  position: 'absolute',
                  left: '12px',
                  top: '8px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#000000',
                  zIndex: 1,
                }}
              >
                Y
              </Typography>
              <TextField
                type="number"
                value={Math.round(logoPosition.y)}
                onChange={(e) => {
                  dispatch(saveToHistory());
                  handleLogoPositionChange('y', e.target.value);
                }}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F5F5F5',
                    borderRadius: '8px',
                    paddingLeft: '32px',
                    '& fieldset': { border: 'none' },
                    '& input': { padding: '8px 12px', fontSize: '14px' },
                  },
                }}
                fullWidth
              />
            </Box>
          </Box>

          {/* Width and Height */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              marginBottom: '12px',
            }}
          >
            <Box sx={{ position: 'relative', flex: 1 }}>
              <Typography
                sx={{
                  position: 'absolute',
                  left: '12px',
                  top: '8px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#000000',
                  zIndex: 1,
                }}
              >
                W
              </Typography>
              <TextField
                type="number"
                value={Math.round(logoSize.width)}
                onChange={(e) => {
                  dispatch(saveToHistory());
                  handleLogoSizeChange('width', e.target.value);
                }}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F5F5F5',
                    borderRadius: '8px',
                    paddingLeft: '32px',
                    '& fieldset': { border: 'none' },
                    '& input': { padding: '8px 12px', fontSize: '14px' },
                  },
                }}
                fullWidth
              />
            </Box>
            <Box sx={{ position: 'relative', flex: 1 }}>
              <Typography
                sx={{
                  position: 'absolute',
                  left: '12px',
                  top: '8px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#000000',
                  zIndex: 1,
                }}
              >
                H
              </Typography>
              <TextField
                type="number"
                value={Math.round(logoSize.height)}
                onChange={(e) => {
                  dispatch(saveToHistory());
                  handleLogoSizeChange('height', e.target.value);
                }}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F5F5F5',
                    borderRadius: '8px',
                    paddingLeft: '32px',
                    '& fieldset': { border: 'none' },
                    '& input': { padding: '8px 12px', fontSize: '14px' },
                  },
                }}
                fullWidth
              />
            </Box>
          </Box>

          {/* Rotation */}
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              alignItems: 'center',
            }}
          >
            <TextField
              type="number"
              value={logoProperties.rotation || 0}
              onChange={(e) =>
                handleLogoPropertyChange('rotation', parseFloat(e.target.value) || 0)
              }
              size="small"
              InputProps={{
                startAdornment: (
                  <Box sx={{ marginRight: '8px', display: 'flex', alignItems: 'center' }}>
                    <RiRotateLockLine size={16} color="#666666" />
                  </Box>
                ),
                endAdornment: (
                  <Typography sx={{ fontSize: '14px', color: '#666', marginLeft: '4px' }}>
                    °
                  </Typography>
                ),
              }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#F5F5F5',
                  borderRadius: '8px',
                  '& fieldset': { border: 'none' },
                  '& input': { padding: '8px 12px', fontSize: '14px' },
                },
              }}
            />
            <IconButton
              size="small"
              onClick={() => {
                const currentRotation = (logoProperties.rotation || 0) + 90;
                handleLogoPropertyChange('rotation', currentRotation % 360);
              }}
              sx={{
                backgroundColor: '#F5F5F5',
                borderRadius: '6px',
                width: '32px',
                height: '32px',
                '&:hover': { backgroundColor: '#E0E0E0' },
              }}
            >
              <RiClockwiseLine size={24} color="#000000" />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Text Properties - Typography */}
      {type === 'text' && (
        <>
          {/* Position Section */}
          <Box sx={{ marginBottom: '24px' }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                fontSize: '14px',
                color: '#000000',
                marginBottom: '12px',
              }}
            >
              Position
            </Typography>
            <Box sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              marginBottom: '12px',
            }}>
              <Box sx={{ position: 'relative', flex: 1 }}>
                <Typography
                  sx={{
                    position: 'absolute',
                    left: '12px',
                    top: '8px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#000000',
                    zIndex: 1,
                  }}
                >
                  X
                </Typography>
                <TextField
                  type="number"
                  value={Math.round(position.x)}
                  onChange={(e) => {
                    dispatch(saveToHistory());
                    handlePositionChange('x', e.target.value);
                  }}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#F5F5F5',
                      borderRadius: '8px',
                      paddingLeft: '32px',
                      '& fieldset': { border: 'none' },
                      '& input': { padding: '8px 12px', fontSize: '14px' },
                    },
                  }}
                  fullWidth
                />
              </Box>
              <Box sx={{ position: 'relative', flex: 1 }}>
                <Typography
                  sx={{
                    position: 'absolute',
                    left: '12px',
                    top: '8px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#000000',
                    zIndex: 1,
                  }}
                >
                  Y
                </Typography>
                <TextField
                  type="number"
                  value={Math.round(position.y)}
                  onChange={(e) => {
                    dispatch(saveToHistory());
                    handlePositionChange('y', e.target.value);
                  }}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#F5F5F5',
                      borderRadius: '8px',
                      paddingLeft: '32px',
                      '& fieldset': { border: 'none' },
                      '& input': { padding: '8px 12px', fontSize: '14px' },
                    },
                  }}
                  fullWidth
                />
              </Box>
            </Box>
            {/* Alignment Buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: 0.5,
                marginBottom: '12px',
                justifyContent: 'space-between',
              }}
            >
              <IconButton
                size="small"
                onClick={() => {
                  // Align Left
                  const canvasDimensions = dimensions[orientation];
                  dispatch(updateElementPosition({ id: selectedElementId, position: { ...position, x: 0 } }));
                }}
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  '&:hover': { backgroundColor: '#E0E0E0' },
                }}
              >
                <RiAlignItemLeftFill size={24} color="#000000" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  // Align Center
                  const canvasDimensions = dimensions[orientation];
                  dispatch(updateElementPosition({ id: selectedElementId, position: { ...position, x: (canvasDimensions.width - size.width) / 2 } }));
                }}
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  '&:hover': { backgroundColor: '#E0E0E0' },
                }}
              >
                <RiAlignItemHorizontalCenterFill size={24} color="#000000" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  // Align Right
                  const canvasDimensions = dimensions[orientation];
                  dispatch(updateElementPosition({ id: selectedElementId, position: { ...position, x: canvasDimensions.width - size.width } }));
                }}
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  '&:hover': { backgroundColor: '#E0E0E0' },
                }}
              >
                <RiAlignItemRightFill size={24} color="#000000" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  // Align Top
                  dispatch(updateElementPosition({ id: selectedElementId, position: { ...position, y: 0 } }));
                }}
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  '&:hover': { backgroundColor: '#E0E0E0' },
                }}
              >
                <RiAlignItemTopFill size={24} color="#000000" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  // Align Middle
                  const canvasDimensions = dimensions[orientation];
                  dispatch(updateElementPosition({ id: selectedElementId, position: { ...position, y: (canvasDimensions.height - size.height) / 2 } }));
                }}
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  '&:hover': { backgroundColor: '#E0E0E0' },
                }}
              >
                <RiAlignItemVerticalCenterFill size={24} color="#000000" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  // Align Bottom
                  const canvasDimensions = dimensions[orientation];
                  dispatch(updateElementPosition({ id: selectedElementId, position: { ...position, y: canvasDimensions.height - size.height } }));
                }}
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  '&:hover': { backgroundColor: '#E0E0E0' },
                }}
              >
                <RiAlignItemBottomFill size={24} color="#000000" />
              </IconButton>
            </Box>

            {/* Width and Height */}
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                marginBottom: '12px',
              }}
            >
              <Box sx={{ position: 'relative', flex: 1 }}>
                <Typography
                  sx={{
                    position: 'absolute',
                    left: '12px',
                    top: '8px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#000000',
                    zIndex: 1,
                  }}
                >
                  W
                </Typography>
                <TextField
                  type="number"
                  value={size.width}
                  onChange={(e) => handleSizeChange('width', e.target.value)}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#F5F5F5',
                      borderRadius: '8px',
                      paddingLeft: '32px',
                      '& fieldset': { border: 'none' },
                      '& input': { padding: '8px 12px', fontSize: '14px' },
                    },
                  }}
                  fullWidth
                />
              </Box>
              <Box sx={{ position: 'relative', flex: 1 }}>
                <Typography
                  sx={{
                    position: 'absolute',
                    left: '12px',
                    top: '8px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#000000',
                    zIndex: 1,
                  }}
                >
                  H
                </Typography>
                <TextField
                  type="number"
                  value={size.height}
                  onChange={(e) => handleSizeChange('height', e.target.value)}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#F5F5F5',
                      borderRadius: '8px',
                      paddingLeft: '32px',
                      '& fieldset': { border: 'none' },
                      '& input': { padding: '8px 12px', fontSize: '14px' },
                    },
                  }}
                  fullWidth
                />
              </Box>
              <IconButton
                size="small"
                onClick={() => {
                  // Fit to screen
                  const canvasDimensions = dimensions[orientation];
                  dispatch(updateElementSize({ id: selectedElementId, size: { width: canvasDimensions.width, height: canvasDimensions.height } }));
                }}
                sx={{
                  backgroundColor: '#6B22D5',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  '&:hover': { backgroundColor: '#5B1DB7' },
                }}
              >
                <RiAspectRatioFill size={16} color="#FFFFFF" />
              </IconButton>
            </Box>

            {/* Rotation and Flip */}
            <Box
              sx={{
                display: 'flex',
                gap: 0.5,
                alignItems: 'center',
              }}
            >
              <TextField
                type="number"
                value={properties.rotation || 0}
                onChange={(e) => handlePropertyChange('rotation', parseFloat(e.target.value) || 0)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <Box sx={{ marginRight: '8px', display: 'flex', alignItems: 'center' }}>
                      <RiRotateLockLine size={16} color="#666666" />
                    </Box>
                  ),
                  endAdornment: (
                    <Typography sx={{ fontSize: '14px', color: '#666', marginLeft: '4px' }}>
                      °
                    </Typography>
                  ),
                }}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F5F5F5',
                    borderRadius: '8px',
                    '& fieldset': { border: 'none' },
                    '& input': { padding: '8px 12px', fontSize: '14px' },
                  },
                }}
              />
              <IconButton
                size="small"
                onClick={() => {
                  const currentRotation = (properties.rotation || 0) + 90;
                  handlePropertyChange('rotation', currentRotation % 360);
                }}
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  '&:hover': { backgroundColor: '#E0E0E0' },
                }}
              >
                <RiClockwiseLine size={24} color="#000000" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  handlePropertyChange('flipVertical', !properties.flipVertical);
                }}
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  '&:hover': { backgroundColor: '#E0E0E0' },
                }}
              >
                <RiFlipVertical2Line size={24} color="#000000" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  handlePropertyChange('flipHorizontal', !properties.flipHorizontal);
                }}
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  '&:hover': { backgroundColor: '#E0E0E0' },
                }}
              >
                <RiFlipHorizontal2Line size={24} color="#000000" />
              </IconButton>
            </Box>
          </Box>

          {/* Separator */}
          <Separator withAccent={false} />

          <Box sx={{ marginBottom: '24px' }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                fontSize: '14px',
                color: '#000000',
                marginBottom: '12px',
              }}
            >
              Title Typography
            </Typography>

            {/* Text Content Input */}
            <TextField
              label="Text Content"
              multiline
              rows={3}
              value={properties.text || ''}
              onChange={(e) => handlePropertyChange('text', e.target.value)}
              fullWidth
              sx={{
                marginTop: '12px',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  marginBottom: '12px',
                  '& fieldset': {
                    border: '1px solid #E0E0E0',
                  },
                },
              }}
            />
            {/* Font Family */}
            <Autocomplete
              size="small"
              options={FONT_FAMILIES}
              value={properties.fontFamily || 'Outfit'}
              onChange={(event, newValue) => {
                handlePropertyChange('fontFamily', newValue || 'Outfit');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search font..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#F5F5F5',
                      borderRadius: '8px',
                      '& fieldset': {
                        border: '1px solid #E0E0E0',
                      },
                      '&:hover fieldset': {
                        border: '1px solid #E0E0E0',
                      },
                      '&.Mui-focused fieldset': {
                        border: '1px solid #E0E0E0',
                      },
                    },
                  }}
                />
              )}
              sx={{ marginBottom: '12px' }}
              disableClearable
            />

            {/* Font Weight and Font Size Row */}
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                marginBottom: '12px',
              }}
            >
              {/* Font Weight */}
              <FormControl size="small" sx={{ flex: 1 }}>
                <Select
                  value={(() => {
                    const weight = properties.fontWeight || 400;
                    const weightLabels = {
                      100: 'thin',
                      200: 'extraLight',
                      300: 'light',
                      400: 'regular',
                      500: 'medium',
                      600: 'semiBold',
                      700: 'bold',
                      800: 'extraBold',
                      900: 'black',
                    };
                    return weightLabels[weight] || 'regular';
                  })()}
                  onChange={(e) => {
                    const weightMap = {
                      thin: 100,
                      extraLight: 200,
                      light: 300,
                      regular: 400,
                      medium: 500,
                      semiBold: 600,
                      bold: 700,
                      extraBold: 800,
                      black: 900,
                    };
                    handlePropertyChange('fontWeight', weightMap[e.target.value] || 400);
                  }}
                  sx={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: '1px solid #E0E0E0',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      border: '1px solid #E0E0E0',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: '1px solid #E0E0E0',
                    },
                  }}
                >
                  <MenuItem value="thin">Thin</MenuItem>
                  <MenuItem value="extraLight">Extra Light</MenuItem>
                  <MenuItem value="light">Light</MenuItem>
                  <MenuItem value="regular">Regular</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="semiBold">Semi Bold</MenuItem>
                  <MenuItem value="bold">Bold</MenuItem>
                  <MenuItem value="extraBold">Extra Bold</MenuItem>
                  <MenuItem value="black">Black</MenuItem>
                </Select>
              </FormControl>

              {/* Font Size */}
              <Box sx={{ position: 'relative', flex: 1 }}>
                <TextField
                  type="number"
                  value={parseInt(properties.fontSize) || 16}
                  onChange={(e) => handlePropertyChange('fontSize', `${e.target.value}px`)}
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        size="small"
                        sx={{
                          padding: '4px',
                          marginRight: '4px',
                        }}
                        title="Line Height / Letter Spacing"
                      >
                        <RiAspectRatioLine size={16} color="#666666" />
                      </IconButton>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      '& fieldset': {
                        border: '1px solid #E0E0E0',
                      },
                    },
                  }}
                  fullWidth
                />
              </Box>
            </Box>

            {/* Text Alignment */}
            <Box
              sx={{
                display: 'flex',
                gap: 0.5,
                alignItems: 'center',
                marginBottom: '12px',
              }}
            >
              <IconButton
                size="small"
                onClick={() => handlePropertyChange('textAlign', 'left')}
                sx={{
                  backgroundColor: (properties.textAlign || 'left') === 'left' ? '#6B22D5' : '#F5F5F5',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  '&:hover': { backgroundColor: (properties.textAlign || 'left') === 'left' ? '#5B1DB7' : '#E0E0E0' },
                }}
              >
                <RiAlignLeft size={20} color={(properties.textAlign || 'left') === 'left' ? '#FFFFFF' : '#000000'} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handlePropertyChange('textAlign', 'center')}
                sx={{
                  backgroundColor: properties.textAlign === 'center' ? '#6B22D5' : '#F5F5F5',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  '&:hover': { backgroundColor: properties.textAlign === 'center' ? '#5B1DB7' : '#E0E0E0' },
                }}
              >
                <RiAlignCenter size={20} color={properties.textAlign === 'center' ? '#FFFFFF' : '#000000'} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handlePropertyChange('textAlign', 'right')}
                sx={{
                  backgroundColor: properties.textAlign === 'right' ? '#6B22D5' : '#F5F5F5',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  '&:hover': { backgroundColor: properties.textAlign === 'right' ? '#5B1DB7' : '#E0E0E0' },
                }}
              >
                <RiAlignRight size={20} color={properties.textAlign === 'right' ? '#FFFFFF' : '#000000'} />
              </IconButton>
            </Box>

            {/* Color, Opacity, Visibility Row */}
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                marginBottom: '12px',
              }}
            >
              {/* Color Picker */}
              <Box
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '8px',
                  padding: '4px',
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  // flex: 1,
                }}
              >
                <Box
                  sx={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '6px',
                    backgroundColor: properties.color || '#000000',
                    border: '1px solid #E0E0E0',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                  onClick={() => setTextColorModalOpen(true)}
                />
                <TextField
                  type="text"
                  value={(properties.color || '#000000').replace('#', '').toUpperCase()}
                  onChange={(e) => {
                    const value = e.target.value.replace('#', '');
                    if (value.length === 6) {
                      handlePropertyChange('color', `#${value}`);
                    }
                  }}
                  size="small"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'transparent',
                      borderRadius: '6px',
                      '& fieldset': { border: 'none' },
                      '& input': {
                        padding: '4px 4px',
                        fontSize: '14px',
                        textTransform: 'uppercase',
                        color: '#000000',
                      },
                    },
                  }}
                />
              </Box>

              {/* Opacity */}
              <Box
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '8px',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  maxWidth: '100px',
                }}
              >
                <TextField
                  type="number"
                  value={Math.round((properties.textOpacity !== undefined ? properties.textOpacity : 1) * 100)}
                  onChange={(e) => handlePropertyChange('textOpacity', Math.max(0, Math.min(100, parseInt(e.target.value) || 100)) / 100)}
                  size="small"
                  sx={{
                    width: '70px',
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'transparent',
                      borderRadius: '6px',
                      '& fieldset': { border: 'none' },
                      '& input': {
                        padding: '4px 4px',
                        fontSize: '14px',
                        textAlign: 'right',
                        color: '#000000',
                      },
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <Typography sx={{ fontSize: '14px', color: '#999', width: 10, maxWidth: 10, textAlign: 'right' }}>
                        %
                      </Typography>
                    ),
                  }}
                />
              </Box>

              {/* Visibility Toggle */}
              <IconButton
                size="small"
                onClick={() => {
                  handlePropertyChange('textVisible', properties.textVisible !== false ? false : true);
                }}
                sx={{
                  padding: '4px',
                  color: '#666666',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                }}
              >
                {properties.textVisible !== false ? (
                  <RiEyeLine size={20} />
                ) : (
                  <RiEyeOffLine size={20} />
                )}
              </IconButton>

              {/* Remove/Minus Icon */}
              <IconButton
                size="small"
                onClick={() => {
                  // Reset text properties or remove text
                  handlePropertyChange('color', '#000000');
                  handlePropertyChange('textOpacity', 1);
                }}
                sx={{
                  padding: '4px',
                  color: '#666666',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                }}
              >
                <RiSubtractLine size={20} />
              </IconButton>
            </Box>

            {/* Separator */}
            <Separator withAccent={false} />

            {/* Fill Color Section */}
            <Box sx={{ marginBottom: '24px' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    fontSize: '14px',
                    color: '#000000',
                  }}
                >
                  Fill Color
                </Typography>
                {properties.textFillColor ? (
                  <IconButton
                    size="small"
                    onClick={() => handlePropertyChange('textFillColor', null)}
                    sx={{
                      backgroundColor: '#F5F5F5',
                      borderRadius: '6px',
                      width: '32px',
                      height: '32px',
                      '&:hover': { backgroundColor: '#E0E0E0' },
                    }}
                  >
                    <RiCloseLine size={16} color="#000000" />
                  </IconButton>
                ) : (
                  <IconButton
                    size="small"
                    onClick={() => handlePropertyChange('textFillColor', '#FFFFFF')}
                    sx={{
                      backgroundColor: '#F5F5F5',
                      borderRadius: '6px',
                      width: '32px',
                      height: '32px',
                      '&:hover': { backgroundColor: '#E0E0E0' },
                    }}
                  >
                    <RiAddLine size={16} color="#000000" />
                  </IconButton>
                )}
              </Box>

              {properties.textFillColor && (
                <Box
                  sx={{
                    backgroundColor: '#F5F5F5',
                    borderRadius: '8px',
                    padding: '8px',
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '6px',
                      backgroundColor: properties.textFillColor,
                      border: '1px solid #E0E0E0',
                      cursor: 'pointer',
                    }}
                    onClick={() => setTextFillColorModalOpen(true)}
                  />
                  <TextField
                    type="text"
                    value={properties.textFillColor.replace('#', '').toUpperCase()}
                    onChange={(e) => {
                      const value = e.target.value.replace('#', '');
                      if (value.length === 6) {
                        handlePropertyChange('textFillColor', `#${value}`);
                      }
                    }}
                    size="small"
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'transparent',
                        borderRadius: '6px',
                        '& fieldset': { border: 'none' },
                        '& input': { padding: '4px 8px', fontSize: '14px', textTransform: 'uppercase' },
                      },
                    }}
                  />
                </Box>
              )}

              {/* {!properties.textFillColor && (
                <Box
                  sx={{
                    height: '1px',
                    backgroundColor: '#E0E0E0',
                    position: 'relative',
                    marginTop: '8px',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      display: 'flex',
                      gap: '4px',
                    }}
                  >
                    <Box
                      sx={{
                        width: '4px',
                        height: '4px',
                        borderRadius: '50%',
                        backgroundColor: '#EC4899',
                      }}
                    />
                    <Box
                      sx={{
                        width: '4px',
                        height: '4px',
                        borderRadius: '50%',
                        backgroundColor: '#EC4899',
                      }}
                    />
                    <Box
                      sx={{
                        width: '4px',
                        height: '4px',
                        borderRadius: '50%',
                        backgroundColor: '#EC4899',
                      }}
                    />
                  </Box>
                </Box>
              )} */}
            </Box>

            {/* Separator */}
            <Separator withAccent={false} />

            {/* Border/Stroke Section */}
            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  fontSize: '14px',
                  color: '#000000',
                  marginBottom: '12px',
                }}
              >
                Border
              </Typography>

              {/* Border Color Row */}
              <Box
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '8px',
                  padding: '4px',
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  marginBottom: '12px',
                }}
              >
                <Box
                  sx={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '6px',
                    backgroundColor: properties.textBorderColor || '#78CE75',
                    border: '1px solid #E0E0E0',
                    cursor: 'pointer',
                  }}
                  onClick={() => setTextBorderColorModalOpen(true)}
                />
                <TextField
                  type="text"
                  value={(properties.textBorderColor || '#78CE75').replace('#', '').toUpperCase()}
                  onChange={(e) => {
                    const value = e.target.value.replace('#', '');
                    if (value.length === 6) {
                      handlePropertyChange('textBorderColor', `#${value}`);
                    }
                  }}
                  size="small"
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'transparent',
                      borderRadius: '6px',
                      '& fieldset': { border: 'none' },
                      '& input': { padding: '4px 8px', fontSize: '14px', textTransform: 'uppercase' },
                    },
                  }}
                />
                <TextField
                  type="number"
                  value={Math.round((properties.textBorderOpacity !== undefined ? properties.textBorderOpacity : 1) * 100)}
                  onChange={(e) => handlePropertyChange('textBorderOpacity', Math.max(0, Math.min(100, parseInt(e.target.value) || 100)) / 100)}
                  size="small"
                  sx={{
                    width: '60px',
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'transparent',
                      borderRadius: '6px',
                      '& fieldset': { border: 'none' },
                      '& input': { padding: '4px 4px', fontSize: '14px', textAlign: 'right' },
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <Typography sx={{ fontSize: '14px', color: '#999', marginRight: '4px' }}>
                        %
                      </Typography>
                    ),
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    handlePropertyChange('textBorderVisible', properties.textBorderVisible !== false ? false : true);
                  }}
                  sx={{
                    padding: '4px',
                    color: '#666666',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                  }}
                >
                  {properties.textBorderVisible !== false ? (
                    <RiEyeLine size={20} />
                  ) : (
                    <RiEyeOffLine size={20} />
                  )}
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => {
                    handlePropertyChange('textBorderColor', null);
                    handlePropertyChange('textBorderWidth', 0);
                  }}
                  sx={{
                    padding: '4px',
                    color: '#666666',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                  }}
                >
                  <RiSubtractLine size={20} />
                </IconButton>
              </Box>

              {/* Border Width and Position */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                <TextField
                  type="number"
                  value={properties.textBorderWidth || 0}
                  onChange={(e) => handlePropertyChange('textBorderWidth', Math.max(0, parseInt(e.target.value) || 0))}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <Box sx={{ marginRight: '8px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                        <Box sx={{ width: '2px', height: '8px', backgroundColor: '#666666' }} />
                        <Box sx={{ width: '3px', height: '10px', backgroundColor: '#666666' }} />
                        <Box sx={{ width: '4px', height: '12px', backgroundColor: '#666666' }} />
                      </Box>
                    ),
                  }}
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#F5F5F5',
                      borderRadius: '8px',
                      '& fieldset': { border: 'none' },
                      '& input': { padding: '8px 12px', fontSize: '14px' },
                    },
                  }}
                />
                <FormControl size="small" sx={{ flex: 1 }}>
                  <Select
                    value={properties.textBorderPosition || 'inside'}
                    onChange={(e) => handlePropertyChange('textBorderPosition', e.target.value)}
                    sx={{
                      backgroundColor: '#F5F5F5',
                      borderRadius: '8px',
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    }}
                  >
                    <MenuItem value="inside">Inside</MenuItem>
                    <MenuItem value="outside">Outside</MenuItem>
                    <MenuItem value="center">Center</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Separator */}
            <Separator withAccent={false} />

            {/* Appearance Section */}
            <Box sx={{ marginBottom: '24px' }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  fontSize: '14px',
                  color: '#000000',
                  marginBottom: '12px',
                }}
              >
                Appearance
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                {/* Opacity */}
                <TextField
                  type="number"
                  value={Math.round((properties.opacity || 1) * 100)}
                  onChange={(e) => handlePropertyChange('opacity', Math.max(0, Math.min(100, parseInt(e.target.value) || 100)) / 100)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <Box sx={{ marginRight: '0px', display: 'flex', alignItems: 'center' }}>
                        <RiContrastLine size={24} color="#666666" />
                      </Box>
                    ),
                    endAdornment: (
                      <Typography sx={{ fontSize: '14px', color: '#666', marginLeft: '0px' }}>
                        %
                      </Typography>
                    ),
                  }}
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#F5F5F5',
                      borderRadius: '8px',
                      border: properties.opacity !== undefined && properties.opacity < 1 ? '2px solid #6B22D5' : 'none',
                      '& fieldset': { border: 'none' },
                      '& input': { padding: '8px 12px', fontSize: '12px' },
                    },
                  }}
                />

                {/* Border Radius */}
                <TextField
                  type="number"
                  value={properties.borderRadius || 0}
                  onChange={(e) => handlePropertyChange('borderRadius', Math.max(0, parseInt(e.target.value) || 0))}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <Box sx={{ marginRight: '8px', display: 'flex', alignItems: 'center' }}>
                        <RiRoundedCorner size={24} />
                      </Box>
                    ),
                  }}
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#F5F5F5',
                      borderRadius: '8px',
                      '& fieldset': { border: 'none' },
                      '& input': { padding: '8px 12px', fontSize: '14px' },
                    },
                  }}
                />

                {/* Individual Border Radius Toggle */}
                <IconButton
                  size="small"
                  onClick={() => {
                    handlePropertyChange('individualBorderRadius', !properties.individualBorderRadius);
                  }}
                  sx={{
                    backgroundColor: properties.individualBorderRadius ? '#6B22D5' : '#F5F5F5',
                    borderRadius: '6px',
                    width: '32px',
                    height: '32px',
                    '&:hover': { backgroundColor: properties.individualBorderRadius ? '#5B1DB7' : '#E0E0E0' },
                  }}
                >
                  <RiFullscreenLine size={24} color={properties.individualBorderRadius ? '#FFFFFF' : '#000000'} />
                </IconButton>
              </Box>

              {/* Individual Border Radius Inputs (shown when enabled) */}
              {properties.individualBorderRadius && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 1,
                    marginTop: '12px',
                  }}
                >
                  <TextField
                    label="Top Left"
                    type="number"
                    value={properties.borderRadiusTopLeft || 0}
                    onChange={(e) => handlePropertyChange('borderRadiusTopLeft', Math.max(0, parseInt(e.target.value) || 0))}
                    size="small"
                  />
                  <TextField
                    label="Top Right"
                    type="number"
                    value={properties.borderRadiusTopRight || 0}
                    onChange={(e) => handlePropertyChange('borderRadiusTopRight', Math.max(0, parseInt(e.target.value) || 0))}
                    size="small"
                  />
                  <TextField
                    label="Bottom Left"
                    type="number"
                    value={properties.borderRadiusBottomLeft || 0}
                    onChange={(e) => handlePropertyChange('borderRadiusBottomLeft', Math.max(0, parseInt(e.target.value) || 0))}
                    size="small"
                  />
                  <TextField
                    label="Bottom Right"
                    type="number"
                    value={properties.borderRadiusBottomRight || 0}
                    onChange={(e) => handlePropertyChange('borderRadiusBottomRight', Math.max(0, parseInt(e.target.value) || 0))}
                    size="small"
                  />
                </Box>
              )}
            </Box>
          </Box>

          {/* Text Fill Color Modal */}
          <ColorPickerModal
            open={textFillColorModalOpen}
            onClose={() => setTextFillColorModalOpen(false)}
            currentColor={properties.textFillColor || '#FFFFFF'}
            onColorSelect={(color) => {
              handlePropertyChange('textFillColor', color);
            }}
            title="Fill Color"
          />

          {/* Text Border Color Modal */}
          <ColorPickerModal
            open={textBorderColorModalOpen}
            onClose={() => setTextBorderColorModalOpen(false)}
            currentColor={properties.textBorderColor || '#78CE75'}
            onColorSelect={(color) => {
              handlePropertyChange('textBorderColor', color);
            }}
            title="Border Color"
          />

          {/* Text Color Modal */}
          <ColorPickerModal
            open={textColorModalOpen}
            onClose={() => setTextColorModalOpen(false)}
            currentColor={properties.color || '#000000'}
            onColorSelect={(color) => {
              handlePropertyChange('color', color);
            }}
            title="Text Color"
          />
        </>
      )}

      {/* Shape Properties - Detailed (for shapes and images) */}
      {(type === 'shape' || type === 'image') && (
        <>
          {/* Position Section */}
          <Box sx={{ marginBottom: '24px' }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                fontSize: '14px',
                color: '#000000',
                marginBottom: '12px',
              }}
            >
              Position
            </Typography>
            <Box sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              marginBottom: '12px',
            }}>
              {/* <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  fontSize: '14px',
                  color: '#000000',
                  marginBottom: '12px',
                }}
              >
                Position
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
                <TextField
                  label="X"
                  type="number"
                  size="small"
                  value={Math.round(position.x)}
                  onChange={(e) => {
                    dispatch(saveToHistory());
                    handlePositionChange('x', e.target.value);
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#F5F5F5',
                      borderRadius: '6px',
                      '& fieldset': { border: 'none' },
                    },
                  }}
                />
                <TextField
                  label="Y"
                  type="number"
                  size="small"
                  value={Math.round(position.y)}
                  onChange={(e) => {
                    dispatch(saveToHistory());
                    handlePositionChange('y', e.target.value);
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#F5F5F5',
                      borderRadius: '6px',
                      '& fieldset': { border: 'none' },
                    },
                  }}
                />
              </Box> */}
              <Box sx={{ position: 'relative', flex: 1 }}>
                <Typography
                  sx={{
                    position: 'absolute',
                    left: '12px',
                    top: '8px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#000000',
                    zIndex: 1,
                  }}
                >
                  X
                </Typography>
                <TextField
                  type="number"
                  value={Math.round(position.x)}
                  onChange={(e) => {
                    dispatch(saveToHistory());
                    handlePositionChange('x', e.target.value);
                  }}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#F5F5F5',
                      borderRadius: '8px',
                      paddingLeft: '32px',
                      '& fieldset': { border: 'none' },
                      '& input': { padding: '8px 12px', fontSize: '14px' },
                    },
                  }}
                  fullWidth
                />
              </Box>
              <Box sx={{ position: 'relative', flex: 1 }}>
                <Typography
                  sx={{
                    position: 'absolute',
                    left: '12px',
                    top: '8px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#000000',
                    zIndex: 1,
                  }}
                >
                  Y
                </Typography>
                <TextField
                  type="number"
                  value={Math.round(position.y)}
                  onChange={(e) => {
                    dispatch(saveToHistory());
                    handlePositionChange('y', e.target.value);
                  }}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#F5F5F5',
                      borderRadius: '8px',
                      paddingLeft: '32px',
                      '& fieldset': { border: 'none' },
                      '& input': { padding: '8px 12px', fontSize: '14px' },
                    },
                  }}
                  fullWidth
                />
              </Box>
            </Box>
            {/* Alignment Buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: 0.5,
                marginBottom: '12px',
                justifyContent: 'space-between',
              }}
            >
              <IconButton
                size="small"
                onClick={() => {
                  // Align Left
                  const canvasDimensions = dimensions[orientation];
                  dispatch(updateElementPosition({ id: selectedElementId, position: { ...position, x: 0 } }));
                }}
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  '&:hover': { backgroundColor: '#E0E0E0' },
                }}
              >
                <RiAlignItemLeftFill size={24} color="#000000" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  // Align Center
                  const canvasDimensions = dimensions[orientation];
                  dispatch(updateElementPosition({ id: selectedElementId, position: { ...position, x: (canvasDimensions.width - size.width) / 2 } }));
                }}
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  '&:hover': { backgroundColor: '#E0E0E0' },
                }}
              >
                <RiAlignItemHorizontalCenterFill size={24} color="#000000" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  // Align Right
                  const canvasDimensions = dimensions[orientation];
                  dispatch(updateElementPosition({ id: selectedElementId, position: { ...position, x: canvasDimensions.width - size.width } }));
                }}
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  '&:hover': { backgroundColor: '#E0E0E0' },
                }}
              >
                <RiAlignItemRightFill size={24} color="#000000" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  // Align Top
                  dispatch(updateElementPosition({ id: selectedElementId, position: { ...position, y: 0 } }));
                }}
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  '&:hover': { backgroundColor: '#E0E0E0' },
                }}
              >
                <RiAlignItemTopFill size={24} color="#000000" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  // Align Middle
                  const canvasDimensions = dimensions[orientation];
                  dispatch(updateElementPosition({ id: selectedElementId, position: { ...position, y: (canvasDimensions.height - size.height) / 2 } }));
                }}
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  '&:hover': { backgroundColor: '#E0E0E0' },
                }}
              >
                <RiAlignItemVerticalCenterFill size={24} color="#000000" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  // Align Bottom
                  const canvasDimensions = dimensions[orientation];
                  dispatch(updateElementPosition({ id: selectedElementId, position: { ...position, y: canvasDimensions.height - size.height } }));
                }}
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  '&:hover': { backgroundColor: '#E0E0E0' },
                }}
              >
                <RiAlignItemBottomFill size={24} color="#000000" />
              </IconButton>
            </Box>

            {/* Width and Height */}
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                marginBottom: '12px',
              }}
            >
              <Box sx={{ position: 'relative', flex: 1 }}>
                <Typography
                  sx={{
                    position: 'absolute',
                    left: '12px',
                    top: '8px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#000000',
                    zIndex: 1,
                  }}
                >
                  W
                </Typography>
                <TextField
                  type="number"
                  value={size.width}
                  onChange={(e) => handleSizeChange('width', e.target.value)}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#F5F5F5',
                      borderRadius: '8px',
                      paddingLeft: '32px',
                      '& fieldset': { border: 'none' },
                      '& input': { padding: '8px 12px', fontSize: '14px' },
                    },
                  }}
                  fullWidth
                />
              </Box>
              <Box sx={{ position: 'relative', flex: 1 }}>
                <Typography
                  sx={{
                    position: 'absolute',
                    left: '12px',
                    top: '8px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#000000',
                    zIndex: 1,
                  }}
                >
                  H
                </Typography>
                <TextField
                  type="number"
                  value={size.height}
                  onChange={(e) => handleSizeChange('height', e.target.value)}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#F5F5F5',
                      borderRadius: '8px',
                      paddingLeft: '32px',
                      '& fieldset': { border: 'none' },
                      '& input': { padding: '8px 12px', fontSize: '14px' },
                    },
                  }}
                  fullWidth
                />
              </Box>
              <IconButton
                size="small"
                onClick={() => {
                  // Fit to screen
                  const canvasDimensions = dimensions[orientation];
                  dispatch(updateElementSize({ id: selectedElementId, size: { width: canvasDimensions.width, height: canvasDimensions.height } }));
                }}
                sx={{
                  backgroundColor: '#6B22D5',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  '&:hover': { backgroundColor: '#5B1DB7' },
                }}
              >
                <RiAspectRatioFill size={16} color="#FFFFFF" />
              </IconButton>
            </Box>

            {/* Rotation and Flip */}
            <Box
              sx={{
                display: 'flex',
                gap: 0.5,
                alignItems: 'center',
              }}
            >
              <TextField
                type="number"
                value={properties.rotation || 0}
                onChange={(e) => handlePropertyChange('rotation', parseFloat(e.target.value) || 0)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <Box sx={{ marginRight: '8px', display: 'flex', alignItems: 'center' }}>
                      <RiRotateLockLine size={16} color="#666666" />
                    </Box>
                  ),
                  endAdornment: (
                    <Typography sx={{ fontSize: '14px', color: '#666', marginLeft: '4px' }}>
                      °
                    </Typography>
                  ),
                }}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F5F5F5',
                    borderRadius: '8px',
                    '& fieldset': { border: 'none' },
                    '& input': { padding: '8px 12px', fontSize: '14px' },
                  },
                }}
              />
              <IconButton
                size="small"
                onClick={() => {
                  const currentRotation = (properties.rotation || 0) + 90;
                  handlePropertyChange('rotation', currentRotation % 360);
                }}
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  '&:hover': { backgroundColor: '#E0E0E0' },
                }}
              >
                <RiClockwiseLine size={24} color="#000000" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  handlePropertyChange('flipVertical', !properties.flipVertical);
                }}
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  '&:hover': { backgroundColor: '#E0E0E0' },
                }}
              >
                <RiFlipVertical2Line size={24} color="#000000" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  handlePropertyChange('flipHorizontal', !properties.flipHorizontal);
                }}
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  '&:hover': { backgroundColor: '#E0E0E0' },
                }}
              >
                <RiFlipHorizontal2Line size={24} color="#000000" />
              </IconButton>
            </Box>
          </Box>

          {/* Separator */}
          <Separator withAccent={false} />

          {/* Appearance Section */}
          <Box sx={{ marginBottom: '24px' }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                fontSize: '14px',
                color: '#000000',
                marginBottom: '12px',
              }}
            >
              Appearance
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
              }}
            >
              {/* Opacity */}
              <TextField
                type="number"
                value={Math.round((properties.opacity || 1) * 100)}
                onChange={(e) => handlePropertyChange('opacity', Math.max(0, Math.min(100, parseInt(e.target.value) || 100)) / 100)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <Box sx={{ marginRight: '0px', display: 'flex', alignItems: 'center' }}>
                      <RiContrastLine size={24} color="#666666" />
                    </Box>
                  ),
                  endAdornment: (
                    <Typography sx={{ fontSize: '14px', color: '#666', marginLeft: '0px' }}>
                      %
                    </Typography>
                  ),
                }}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F5F5F5',
                    borderRadius: '8px',
                    border: properties.opacity !== undefined && properties.opacity < 1 ? '2px solid #6B22D5' : 'none',
                    '& fieldset': { border: 'none' },
                    '& input': { padding: '8px 12px', fontSize: '12px' },
                  },
                }}
              />

              {/* Border Radius */}
              <TextField
                type="number"
                value={properties.borderRadius || 0}
                onChange={(e) => handlePropertyChange('borderRadius', Math.max(0, parseInt(e.target.value) || 0))}
                size="small"
                InputProps={{
                  startAdornment: (
                    <Box sx={{ marginRight: '8px', display: 'flex', alignItems: 'center' }}>
                      <RiRoundedCorner size={24} />
                    </Box>
                  ),
                }}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F5F5F5',
                    borderRadius: '8px',
                    '& fieldset': { border: 'none' },
                    '& input': { padding: '8px 12px', fontSize: '14px' },
                  },
                }}
              />

              {/* Individual Border Radius Toggle */}
              <IconButton
                size="small"
                onClick={() => {
                  handlePropertyChange('individualBorderRadius', !properties.individualBorderRadius);
                }}
                sx={{
                  backgroundColor: properties.individualBorderRadius ? '#6B22D5' : '#F5F5F5',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  '&:hover': { backgroundColor: properties.individualBorderRadius ? '#5B1DB7' : '#E0E0E0' },
                }}
              >
                <RiFullscreenLine size={24} color={properties.individualBorderRadius ? '#FFFFFF' : '#000000'} />
              </IconButton>
            </Box>

            {/* Individual Border Radius Inputs (shown when enabled) */}
            {properties.individualBorderRadius && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 1,
                  marginTop: '12px',
                }}
              >
                <TextField
                  label="Top Left"
                  type="number"
                  value={properties.borderRadiusTopLeft || 0}
                  onChange={(e) => handlePropertyChange('borderRadiusTopLeft', Math.max(0, parseInt(e.target.value) || 0))}
                  size="small"
                />
                <TextField
                  label="Top Right"
                  type="number"
                  value={properties.borderRadiusTopRight || 0}
                  onChange={(e) => handlePropertyChange('borderRadiusTopRight', Math.max(0, parseInt(e.target.value) || 0))}
                  size="small"
                />
                <TextField
                  label="Bottom Left"
                  type="number"
                  value={properties.borderRadiusBottomLeft || 0}
                  onChange={(e) => handlePropertyChange('borderRadiusBottomLeft', Math.max(0, parseInt(e.target.value) || 0))}
                  size="small"
                />
                <TextField
                  label="Bottom Right"
                  type="number"
                  value={properties.borderRadiusBottomRight || 0}
                  onChange={(e) => handlePropertyChange('borderRadiusBottomRight', Math.max(0, parseInt(e.target.value) || 0))}
                  size="small"
                />
              </Box>
            )}
          </Box>

          {/* Separator */}
          <Separator withAccent={false} />

          {/* Fill Color Section */}
          <Box sx={{ marginBottom: '24px' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px',
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  fontSize: '14px',
                  color: '#000000',
                }}
              >
                Fill Color
              </Typography>
              {properties.fillColor && properties.fillColor !== 'transparent' ? (
                <IconButton
                  size="small"
                  onClick={() => {
                    // When removing fillColor, set it back to transparent and ensure white border
                    handlePropertyChange('fillColor', 'transparent');
                    if (!properties.strokeColor || properties.strokeColor !== '#FFFFFF') {
                      handlePropertyChange('strokeColor', '#FFFFFF');
                    }
                    if (!properties.strokeWidth || properties.strokeWidth !== 1) {
                      handlePropertyChange('strokeWidth', 1);
                    }
                    if (properties.strokeVisible === false) {
                      handlePropertyChange('strokeVisible', true);
                    }
                  }}
                  sx={{
                    backgroundColor: '#F5F5F5',
                    borderRadius: '6px',
                    width: '32px',
                    height: '32px',
                    '&:hover': { backgroundColor: '#E0E0E0' },
                  }}
                >
                  <RiCloseLine size={16} color="#000000" />
                </IconButton>
              ) : (
                <IconButton
                  size="small"
                  onClick={() => handlePropertyChange('fillColor', '#6B22D5')}
                  sx={{
                    backgroundColor: '#F5F5F5',
                    borderRadius: '6px',
                    width: '32px',
                    height: '32px',
                    '&:hover': { backgroundColor: '#E0E0E0' },
                  }}
                >
                  <RiAddLine size={16} color="#000000" />
                </IconButton>
              )}
            </Box>

            {properties.fillColor && properties.fillColor !== 'transparent' && (
              <Box
                sx={{
                  backgroundColor: '#F5F5F5',
                  borderRadius: '8px',
                  padding: '8px',
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '6px',
                    backgroundColor: properties.fillColor,
                    border: '1px solid #E0E0E0',
                    cursor: 'pointer',
                  }}
                  onClick={() => setFillColorModalOpen(true)}
                />
                <TextField
                  type="text"
                  value={properties.fillColor.replace('#', '').toUpperCase()}
                  onChange={(e) => {
                    const value = e.target.value.replace('#', '');
                    if (value.length === 6) {
                      handlePropertyChange('fillColor', `#${value}`);
                    }
                  }}
                  size="small"
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'transparent',
                      borderRadius: '6px',
                      '& fieldset': { border: 'none' },
                      '& input': { padding: '4px 8px', fontSize: '14px', textTransform: 'uppercase' },
                    },
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Separator */}
          <Separator withAccent={false} />

          {/* Stroke Color Section */}
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                fontSize: '14px',
                color: '#000000',
                marginBottom: '12px',
              }}
            >
              Stroke Color
            </Typography>

            {/* Stroke Color Row */}
            <Box
              sx={{
                // backgroundColor: '#F5F5F5',
                // borderRadius: '8px',
                // padding: '8px',
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                marginBottom: '12px',
              }}
            >
              <Box sx={{
                backgroundColor: '#F5F5F5',
                borderRadius: '8px',
                padding: '8px',
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                maxWidth: 130,
                // marginBottom: '12px',
              }}>
                <Box
                  sx={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '6px',
                    backgroundColor: properties.strokeColor || '#78CE75',
                    border: '1px solid #E0E0E0',
                    cursor: 'pointer',
                  }}
                  onClick={() => setStrokeColorModalOpen(true)}
                />
                <TextField
                  type="text"
                  value={(properties.strokeColor || '#78CE75').replace('#', '').toUpperCase()}
                  onChange={(e) => {
                    const value = e.target.value.replace('#', '');
                    if (value.length === 6) {
                      handlePropertyChange('strokeColor', `#${value}`);
                    }
                  }}
                  size="small"
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'transparent',
                      borderRadius: '6px',
                      '& fieldset': { border: 'none' },
                      '& input': { padding: '4px 8px', fontSize: '14px', textTransform: 'uppercase' },
                    },
                  }}
                />
              </Box>
              <Box sx={{
                backgroundColor: '#F5F5F5',
                borderRadius: '8px',
                padding: '8px',
                display: 'flex',
                // gap: 1,
                alignItems: 'center',
                maxWidth: 69,
                // marginBottom: '12px',
              }}>
                <TextField
                  type="number"
                  value={Math.round((properties.strokeOpacity || 1) * 100)}
                  onChange={(e) => handlePropertyChange('strokeOpacity', Math.max(0, Math.min(100, parseInt(e.target.value) || 100)) / 100)}
                  size="small"
                  sx={{
                    width: '60px',
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'transparent',
                      borderRadius: '6px',
                      '& fieldset': { border: 'none' },
                      '& input': { padding: '4px 4px', fontSize: '14px', textAlign: 'right' },
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <Typography sx={{ fontSize: '14px', color: '#999', width: 10 }}>
                        %
                      </Typography>
                    ),
                  }}
                />
              </Box>
              <IconButton
                size="small"
                onClick={() => {
                  handlePropertyChange('strokeVisible', !properties.strokeVisible);
                }}
                sx={{
                  padding: '4px',
                  color: '#666666',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                }}
              >
                {properties.strokeVisible !== false ? (
                  <RiEyeLine size={20} />
                ) : (
                  <RiEyeOffLine size={20} />
                )}
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  handlePropertyChange('strokeColor', null);
                }}
                sx={{
                  padding: '4px',
                  color: '#666666',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                }}
              >
                <RiSubtractLine size={20} />
              </IconButton>
            </Box>

            {/* Stroke Width and Position */}
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <TextField
                type="number"
                value={properties.strokeWidth || 0}
                onChange={(e) => handlePropertyChange('strokeWidth', Math.max(0, parseInt(e.target.value) || 0))}
                size="small"
                InputProps={{
                  startAdornment: (
                    <Box sx={{ marginRight: '8px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                      <Box sx={{ width: '2px', height: '8px', backgroundColor: '#666666' }} />
                      <Box sx={{ width: '3px', height: '10px', backgroundColor: '#666666' }} />
                      <Box sx={{ width: '4px', height: '12px', backgroundColor: '#666666' }} />
                    </Box>
                  ),
                }}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F5F5F5',
                    borderRadius: '8px',
                    '& fieldset': { border: 'none' },
                    '& input': { padding: '8px 12px', fontSize: '14px' },
                  },
                }}
              />
              <FormControl size="small" sx={{ flex: 1 }}>
                <Select
                  value={properties.strokePosition || 'inside'}
                  onChange={(e) => handlePropertyChange('strokePosition', e.target.value)}
                  sx={{
                    backgroundColor: '#F5F5F5',
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  }}
                >
                  <MenuItem value="inside">Inside</MenuItem>
                  <MenuItem value="outside">Outside</MenuItem>
                  <MenuItem value="center">Center</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Fill Color Modal */}
          <ColorPickerModal
            open={fillColorModalOpen}
            onClose={() => setFillColorModalOpen(false)}
            currentColor={properties.fillColor || '#6B22D5'}
            onColorSelect={(color) => {
              handlePropertyChange('fillColor', color);
            }}
            title="Fill Color"
          />

          {/* Stroke Color Modal */}
          <ColorPickerModal
            open={strokeColorModalOpen}
            onClose={() => setStrokeColorModalOpen(false)}
            currentColor={properties.strokeColor || '#78CE75'}
            onColorSelect={(color) => {
              handlePropertyChange('strokeColor', color);
            }}
            title="Stroke Color"
          />

          {/* Box Shadow Color Modal */}
          <ColorPickerModal
            open={boxShadowColorModalOpen}
            onClose={() => setBoxShadowColorModalOpen(false)}
            currentColor={properties.boxShadowColor || 'rgba(0,0,0,0.25)'}
            onColorSelect={(color) => {
              handlePropertyChange('boxShadowColor', color);
            }}
            title="Shadow Color"
          />

          {/* Box Shadow Section */}
          <Separator />
          <Box sx={{ marginBottom: '24px' }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                fontSize: '14px',
                color: '#000000',
                marginBottom: '12px',
              }}
            >
              Box Shadow
            </Typography>

            {/* Shadow Visibility Toggle */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '12px',
                padding: '8px',
                backgroundColor: '#F5F5F5',
                borderRadius: '8px',
              }}
            >
              <Typography variant="body2" sx={{ fontSize: '12px', color: '#666' }}>
                Enable Shadow
              </Typography>
              <Button
                size="small"
                onClick={() => handlePropertyChange('boxShadowVisible', !(properties.boxShadowVisible !== false))}
                sx={{
                  backgroundColor: properties.boxShadowVisible !== false ? '#6B22D5' : '#F5F5F5',
                  borderRadius: '6px',
                  minWidth: 'auto',
                  padding: '4px 12px',
                  '&:hover': {
                    backgroundColor: properties.boxShadowVisible !== false ? '#5B1DB7' : '#E0E0E0',
                  },
                }}
              >
                {properties.boxShadowVisible !== false ? (
                  <RiEyeLine size={20} color="#FFFFFF" />
                ) : (
                  <RiEyeOffLine size={20} color="#666" />
                )}
              </Button>
            </Box>

            {properties.boxShadowVisible !== false && (
              <>
                {/* Shadow Color */}
                <Box sx={{ marginBottom: '12px' }}>
                  <Typography variant="caption" sx={{ display: 'block', marginBottom: '8px', color: '#666', fontSize: '12px' }}>
                    Shadow Color
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '6px',
                        backgroundColor: properties.boxShadowColor || 'rgba(0,0,0,0.25)',
                        border: '1px solid #E0E0E0',
                        cursor: 'pointer',
                      }}
                      onClick={() => setBoxShadowColorModalOpen(true)}
                    />
                    <TextField
                      size="small"
                      placeholder="rgba(0,0,0,0.25)"
                      value={properties.boxShadowColor || 'rgba(0,0,0,0.25)'}
                      onChange={(e) => handlePropertyChange('boxShadowColor', e.target.value)}
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#F5F5F5',
                          borderRadius: '6px',
                          '& fieldset': { border: 'none' },
                        },
                      }}
                    />
                  </Box>
                </Box>

                {/* Shadow Offset X */}
                <Box sx={{ marginBottom: '12px' }}>
                  <Typography variant="caption" sx={{ display: 'block', marginBottom: '8px', color: '#666', fontSize: '12px' }}>
                    Offset X
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    value={properties.boxShadowOffsetX || 0}
                    onChange={(e) => handlePropertyChange('boxShadowOffsetX', parseInt(e.target.value) || 0)}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#F5F5F5',
                        borderRadius: '6px',
                        '& fieldset': { border: 'none' },
                      },
                    }}
                  />
                </Box>

                {/* Shadow Offset Y */}
                <Box sx={{ marginBottom: '12px' }}>
                  <Typography variant="caption" sx={{ display: 'block', marginBottom: '8px', color: '#666', fontSize: '12px' }}>
                    Offset Y
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    value={properties.boxShadowOffsetY || 0}
                    onChange={(e) => handlePropertyChange('boxShadowOffsetY', parseInt(e.target.value) || 0)}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#F5F5F5',
                        borderRadius: '6px',
                        '& fieldset': { border: 'none' },
                      },
                    }}
                  />
                </Box>

                {/* Shadow Blur / Radius */}
                <Box sx={{ marginBottom: '12px' }}>
                  <Typography variant="caption" sx={{ display: 'block', marginBottom: '8px', color: '#666', fontSize: '12px' }}>
                    Shadow Radius (Blur)
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    value={properties.boxShadowBlur || 0}
                    onChange={(e) => handlePropertyChange('boxShadowBlur', Math.max(0, parseInt(e.target.value) || 0))}
                    placeholder="0"
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#F5F5F5',
                        borderRadius: '6px',
                        '& fieldset': { border: 'none' },
                      },
                    }}
                  />
                </Box>

                {/* Shadow Spread */}
                <Box sx={{ marginBottom: '12px' }}>
                  <Typography variant="caption" sx={{ display: 'block', marginBottom: '8px', color: '#666', fontSize: '12px' }}>
                    Spread
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    value={properties.boxShadowSpread || 0}
                    onChange={(e) => handlePropertyChange('boxShadowSpread', parseInt(e.target.value) || 0)}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#F5F5F5',
                        borderRadius: '6px',
                        '& fieldset': { border: 'none' },
                      },
                    }}
                  />
                </Box>
              </>
            )}
          </Box>
        </>
      )}

      {/* Menu Item Properties */}
      {type === 'menuItem' && (
        <Box sx={{ marginBottom: '24px' }}>
          {/* Tab Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              borderBottom: '1px solid #E5E7EB',
              mb: 2,
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
            }}
          >
            <Tabs
              value={menuItemTab}
              onChange={(e, newValue) => setMenuItemTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 'auto',
                width: '100%',
                '& .MuiTab-root': {
                  minHeight: 'auto',
                  padding: '8px 16px',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#666',
                  whiteSpace: 'nowrap',
                  '&.Mui-selected': {
                    color: '#000',
                    fontWeight: 600,
                  },
                },
                '& .MuiTabs-indicator': {
                  height: 2,
                },
                '& .MuiTabs-scrollButtons': {
                  width: 32,
                  '&.Mui-disabled': {
                    opacity: 0.3,
                  },
                },
              }}
            >
              <Tab
                icon={<RiEdit2Fill size={24} />}
                iconPosition="end"
                label=""
                sx={{ minWidth: 'auto', padding: '8px' }}
                onClick={() => setMenuItemTab(0)}
              />
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />
              <Tab label="Item" onClick={() => setMenuItemTab(1)} />
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />
              <Tab label="Price" onClick={() => setMenuItemTab(2)} />
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />
              <Tab label="Description" onClick={() => setMenuItemTab(3)} />
            </Tabs>
          </Box>

          {/* Tab Content */}
          {menuItemTab === 0 && (
            <>
              {/* Position Section */}
              <Box sx={{ marginBottom: '24px' }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    fontSize: '14px',
                    color: '#000000',
                    marginBottom: '12px',
                  }}
                >
                  Position
                </Typography>
                <Box sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  marginBottom: '12px',
                }}>
                  {/* <Typography
                   variant="subtitle1"
                   sx={{
                     fontWeight: 600,
                     fontSize: '14px',
                     color: '#000000',
                     marginBottom: '12px',
                   }}
                 >
                   Position
                 </Typography>
                 <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
                   <TextField
                     label="X"
                     type="number"
                     size="small"
                     value={Math.round(position.x)}
                     onChange={(e) => {
                       dispatch(saveToHistory());
                       handlePositionChange('x', e.target.value);
                     }}
                     sx={{
                       '& .MuiOutlinedInput-root': {
                         backgroundColor: '#F5F5F5',
                         borderRadius: '6px',
                         '& fieldset': { border: 'none' },
                       },
                     }}
                   />
                   <TextField
                     label="Y"
                     type="number"
                     size="small"
                     value={Math.round(position.y)}
                     onChange={(e) => {
                       dispatch(saveToHistory());
                       handlePositionChange('y', e.target.value);
                     }}
                     sx={{
                       '& .MuiOutlinedInput-root': {
                         backgroundColor: '#F5F5F5',
                         borderRadius: '6px',
                         '& fieldset': { border: 'none' },
                       },
                     }}
                   />
                 </Box> */}
                  <Box sx={{ position: 'relative', flex: 1 }}>
                    <Typography
                      sx={{
                        position: 'absolute',
                        left: '12px',
                        top: '8px',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#000000',
                        zIndex: 1,
                      }}
                    >
                      X
                    </Typography>
                    <TextField
                      type="number"
                      value={Math.round(position.x)}
                      onChange={(e) => {
                        dispatch(saveToHistory());
                        handlePositionChange('x', e.target.value);
                      }}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#F5F5F5',
                          borderRadius: '8px',
                          paddingLeft: '32px',
                          '& fieldset': { border: 'none' },
                          '& input': { padding: '8px 12px', fontSize: '14px' },
                        },
                      }}
                      fullWidth
                    />
                  </Box>
                  <Box sx={{ position: 'relative', flex: 1 }}>
                    <Typography
                      sx={{
                        position: 'absolute',
                        left: '12px',
                        top: '8px',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#000000',
                        zIndex: 1,
                      }}
                    >
                      Y
                    </Typography>
                    <TextField
                      type="number"
                      value={Math.round(position.y)}
                      onChange={(e) => {
                        dispatch(saveToHistory());
                        handlePositionChange('y', e.target.value);
                      }}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#F5F5F5',
                          borderRadius: '8px',
                          paddingLeft: '32px',
                          '& fieldset': { border: 'none' },
                          '& input': { padding: '8px 12px', fontSize: '14px' },
                        },
                      }}
                      fullWidth
                    />
                  </Box>
                </Box>
                {/* Alignment Buttons */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    marginBottom: '12px',
                    justifyContent: 'space-between',
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => {
                      // Align Left
                      const canvasDimensions = dimensions[orientation];
                      dispatch(updateElementPosition({ id: selectedElementId, position: { ...position, x: 0 } }));
                    }}
                    sx={{
                      backgroundColor: '#F5F5F5',
                      borderRadius: '6px',
                      width: '32px',
                      height: '32px',
                      '&:hover': { backgroundColor: '#E0E0E0' },
                    }}
                  >
                    <RiAlignItemLeftFill size={24} color="#000000" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      // Align Center
                      const canvasDimensions = dimensions[orientation];
                      dispatch(updateElementPosition({ id: selectedElementId, position: { ...position, x: (canvasDimensions.width - size.width) / 2 } }));
                    }}
                    sx={{
                      backgroundColor: '#F5F5F5',
                      borderRadius: '6px',
                      width: '32px',
                      height: '32px',
                      '&:hover': { backgroundColor: '#E0E0E0' },
                    }}
                  >
                    <RiAlignItemHorizontalCenterFill size={24} color="#000000" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      // Align Right
                      const canvasDimensions = dimensions[orientation];
                      dispatch(updateElementPosition({ id: selectedElementId, position: { ...position, x: canvasDimensions.width - size.width } }));
                    }}
                    sx={{
                      backgroundColor: '#F5F5F5',
                      borderRadius: '6px',
                      width: '32px',
                      height: '32px',
                      '&:hover': { backgroundColor: '#E0E0E0' },
                    }}
                  >
                    <RiAlignItemRightFill size={24} color="#000000" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      // Align Top
                      dispatch(updateElementPosition({ id: selectedElementId, position: { ...position, y: 0 } }));
                    }}
                    sx={{
                      backgroundColor: '#F5F5F5',
                      borderRadius: '6px',
                      width: '32px',
                      height: '32px',
                      '&:hover': { backgroundColor: '#E0E0E0' },
                    }}
                  >
                    <RiAlignItemTopFill size={24} color="#000000" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      // Align Middle
                      const canvasDimensions = dimensions[orientation];
                      dispatch(updateElementPosition({ id: selectedElementId, position: { ...position, y: (canvasDimensions.height - size.height) / 2 } }));
                    }}
                    sx={{
                      backgroundColor: '#F5F5F5',
                      borderRadius: '6px',
                      width: '32px',
                      height: '32px',
                      '&:hover': { backgroundColor: '#E0E0E0' },
                    }}
                  >
                    <RiAlignItemVerticalCenterFill size={24} color="#000000" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      // Align Bottom
                      const canvasDimensions = dimensions[orientation];
                      dispatch(updateElementPosition({ id: selectedElementId, position: { ...position, y: canvasDimensions.height - size.height } }));
                    }}
                    sx={{
                      backgroundColor: '#F5F5F5',
                      borderRadius: '6px',
                      width: '32px',
                      height: '32px',
                      '&:hover': { backgroundColor: '#E0E0E0' },
                    }}
                  >
                    <RiAlignItemBottomFill size={24} color="#000000" />
                  </IconButton>
                </Box>

                {/* Width and Height */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    marginBottom: '12px',
                  }}
                >
                  <Box sx={{ position: 'relative', flex: 1 }}>
                    <Typography
                      sx={{
                        position: 'absolute',
                        left: '12px',
                        top: '8px',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#000000',
                        zIndex: 1,
                      }}
                    >
                      W
                    </Typography>
                    <TextField
                      type="number"
                      value={size.width}
                      onChange={(e) => handleSizeChange('width', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#F5F5F5',
                          borderRadius: '8px',
                          paddingLeft: '32px',
                          '& fieldset': { border: 'none' },
                          '& input': { padding: '8px 12px', fontSize: '14px' },
                        },
                      }}
                      fullWidth
                    />
                  </Box>
                  <Box sx={{ position: 'relative', flex: 1 }}>
                    <Typography
                      sx={{
                        position: 'absolute',
                        left: '12px',
                        top: '8px',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#000000',
                        zIndex: 1,
                      }}
                    >
                      H
                    </Typography>
                    <TextField
                      type="number"
                      value={size.height}
                      onChange={(e) => handleSizeChange('height', e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#F5F5F5',
                          borderRadius: '8px',
                          paddingLeft: '32px',
                          '& fieldset': { border: 'none' },
                          '& input': { padding: '8px 12px', fontSize: '14px' },
                        },
                      }}
                      fullWidth
                    />
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => {
                      // Fit to screen
                      const canvasDimensions = dimensions[orientation];
                      dispatch(updateElementSize({ id: selectedElementId, size: { width: canvasDimensions.width, height: canvasDimensions.height } }));
                    }}
                    sx={{
                      backgroundColor: '#6B22D5',
                      borderRadius: '6px',
                      width: '32px',
                      height: '32px',
                      '&:hover': { backgroundColor: '#5B1DB7' },
                    }}
                  >
                    <RiAspectRatioFill size={16} color="#FFFFFF" />
                  </IconButton>
                </Box>

                {/* Rotation and Flip */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    alignItems: 'center',
                  }}
                >
                  <TextField
                    type="number"
                    value={properties.rotation || 0}
                    onChange={(e) => handlePropertyChange('rotation', parseFloat(e.target.value) || 0)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ marginRight: '8px', display: 'flex', alignItems: 'center' }}>
                          <RiRotateLockLine size={16} color="#666666" />
                        </Box>
                      ),
                      endAdornment: (
                        <Typography sx={{ fontSize: '14px', color: '#666', marginLeft: '4px' }}>
                          °
                        </Typography>
                      ),
                    }}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#F5F5F5',
                        borderRadius: '8px',
                        '& fieldset': { border: 'none' },
                        '& input': { padding: '8px 12px', fontSize: '14px' },
                      },
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      const currentRotation = (properties.rotation || 0) + 90;
                      handlePropertyChange('rotation', currentRotation % 360);
                    }}
                    sx={{
                      backgroundColor: '#F5F5F5',
                      borderRadius: '6px',
                      width: '32px',
                      height: '32px',
                      '&:hover': { backgroundColor: '#E0E0E0' },
                    }}
                  >
                    <RiClockwiseLine size={24} color="#000000" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      handlePropertyChange('flipVertical', !properties.flipVertical);
                    }}
                    sx={{
                      backgroundColor: '#F5F5F5',
                      borderRadius: '6px',
                      width: '32px',
                      height: '32px',
                      '&:hover': { backgroundColor: '#E0E0E0' },
                    }}
                  >
                    <RiFlipVertical2Line size={24} color="#000000" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      handlePropertyChange('flipHorizontal', !properties.flipHorizontal);
                    }}
                    sx={{
                      backgroundColor: '#F5F5F5',
                      borderRadius: '6px',
                      width: '32px',
                      height: '32px',
                      '&:hover': { backgroundColor: '#E0E0E0' },
                    }}
                  >
                    <RiFlipHorizontal2Line size={24} color="#000000" />
                  </IconButton>
                </Box>
              </Box>

              {/* Separator */}
              <Separator withAccent={false} />

              {/* Appearance Section */}
              <Box sx={{ marginBottom: '24px' }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    fontSize: '14px',
                    color: '#000000',
                    marginBottom: '12px',
                  }}
                >
                  Appearance
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                  }}
                >
                  {/* Opacity */}
                  <TextField
                    type="number"
                    value={Math.round((properties.opacity || 1) * 100)}
                    onChange={(e) => handlePropertyChange('opacity', Math.max(0, Math.min(100, parseInt(e.target.value) || 100)) / 100)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ marginRight: '0px', display: 'flex', alignItems: 'center' }}>
                          <RiContrastLine size={24} color="#666666" />
                        </Box>
                      ),
                      endAdornment: (
                        <Typography sx={{ fontSize: '14px', color: '#666', marginLeft: '0px' }}>
                          %
                        </Typography>
                      ),
                    }}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#F5F5F5',
                        borderRadius: '8px',
                        border: properties.opacity !== undefined && properties.opacity < 1 ? '2px solid #6B22D5' : 'none',
                        '& fieldset': { border: 'none' },
                        '& input': { padding: '8px 12px', fontSize: '12px' },
                      },
                    }}
                  />

                  {/* Border Radius */}
                  <TextField
                    type="number"
                    value={properties.borderRadius || 0}
                    onChange={(e) => handlePropertyChange('borderRadius', Math.max(0, parseInt(e.target.value) || 0))}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ marginRight: '8px', display: 'flex', alignItems: 'center' }}>
                          <RiRoundedCorner size={24} />
                        </Box>
                      ),
                    }}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#F5F5F5',
                        borderRadius: '8px',
                        '& fieldset': { border: 'none' },
                        '& input': { padding: '8px 12px', fontSize: '14px' },
                      },
                    }}
                  />

                  {/* Individual Border Radius Toggle */}
                  <IconButton
                    size="small"
                    onClick={() => {
                      handlePropertyChange('individualBorderRadius', !properties.individualBorderRadius);
                    }}
                    sx={{
                      backgroundColor: properties.individualBorderRadius ? '#6B22D5' : '#F5F5F5',
                      borderRadius: '6px',
                      width: '32px',
                      height: '32px',
                      '&:hover': { backgroundColor: properties.individualBorderRadius ? '#5B1DB7' : '#E0E0E0' },
                    }}
                  >
                    <RiFullscreenLine size={24} color={properties.individualBorderRadius ? '#FFFFFF' : '#000000'} />
                  </IconButton>
                </Box>

                {/* Individual Border Radius Inputs (shown when enabled) */}
                {properties.individualBorderRadius && (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 1,
                      marginTop: '12px',
                    }}
                  >
                    <TextField
                      label="Top Left"
                      type="number"
                      value={properties.borderRadiusTopLeft || 0}
                      onChange={(e) => handlePropertyChange('borderRadiusTopLeft', Math.max(0, parseInt(e.target.value) || 0))}
                      size="small"
                    />
                    <TextField
                      label="Top Right"
                      type="number"
                      value={properties.borderRadiusTopRight || 0}
                      onChange={(e) => handlePropertyChange('borderRadiusTopRight', Math.max(0, parseInt(e.target.value) || 0))}
                      size="small"
                    />
                    <TextField
                      label="Bottom Left"
                      type="number"
                      value={properties.borderRadiusBottomLeft || 0}
                      onChange={(e) => handlePropertyChange('borderRadiusBottomLeft', Math.max(0, parseInt(e.target.value) || 0))}
                      size="small"
                    />
                    <TextField
                      label="Bottom Right"
                      type="number"
                      value={properties.borderRadiusBottomRight || 0}
                      onChange={(e) => handlePropertyChange('borderRadiusBottomRight', Math.max(0, parseInt(e.target.value) || 0))}
                      size="small"
                    />
                  </Box>
                )}
              </Box>

              {/* Separator */}
              <Separator withAccent={false} />

              {/* Menu Item Spacing Section */}
              <Box sx={{ marginBottom: '24px' }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    fontSize: '14px',
                    color: '#000000',
                    marginBottom: '12px',
                  }}
                >
                  Item Spacing
                </Typography>
                <Box sx={{ marginBottom: '12px' }}>
                  <Typography variant="caption" sx={{ display: 'block', marginBottom: '8px', color: '#666', fontSize: '12px' }}>
                    Gap Between Items
                  </Typography>
                  <TextField
                    type="number"
                    value={properties.itemGap || 24}
                    onChange={(e) => handlePropertyChange('itemGap', Math.max(0, parseInt(e.target.value) || 24))}
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <Typography sx={{ fontSize: '14px', color: '#666', marginLeft: '4px' }}>
                          px
                        </Typography>
                      ),
                    }}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#F5F5F5',
                        borderRadius: '8px',
                        '& fieldset': { border: 'none' },
                        '& input': { padding: '8px 12px', fontSize: '14px' },
                      },
                    }}
                  />
                </Box>
              </Box>

              {/* Separator */}
              <Separator withAccent={false} />

              {/* Show Description Toggle */}
              <Box sx={{ marginBottom: '24px' }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    fontSize: '14px',
                    color: '#000000',
                    marginBottom: '12px',
                  }}
                >
                  Description
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px',
                    backgroundColor: '#F5F5F5',
                    borderRadius: '8px',
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: '12px', color: '#666' }}>
                    Show Description
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => {
                      const currentValue = properties.descriptionVisible !== false; // true if undefined or true
                      handlePropertyChange('descriptionVisible', !currentValue);
                    }}
                    sx={{
                      backgroundColor: properties.descriptionVisible !== false ? '#6B22D5' : '#F5F5F5',
                      borderRadius: '6px',
                      minWidth: 'auto',
                      padding: '4px 12px',
                      '&:hover': {
                        backgroundColor: properties.descriptionVisible !== false ? '#5B1DB7' : '#E0E0E0',
                      },
                    }}
                  >
                    {properties.descriptionVisible !== false ? (
                      <RiEyeLine size={20} color="#FFFFFF" />
                    ) : (
                      <RiEyeOffLine size={20} color="#666" />
                    )}
                  </Button>
                </Box>
              </Box>

              {/* Separator */}
              <Separator withAccent={false} />

              {/* Background Color Section (Menu Item Container) */}
              <Box sx={{ marginBottom: '24px' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px',
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      fontSize: '14px',
                      color: '#000000',
                    }}
                  >
                    Background Color
                  </Typography>
                  {properties.backgroundColor ? (
                    <IconButton
                      size="small"
                      onClick={() => handlePropertyChange('backgroundColor', 'rgba(255, 255, 255, 0.95)')}
                      sx={{
                        backgroundColor: '#F5F5F5',
                        borderRadius: '6px',
                        width: '32px',
                        height: '32px',
                        '&:hover': { backgroundColor: '#E0E0E0' },
                      }}
                    >
                      <RiCloseLine size={16} color="#000000" />
                    </IconButton>
                  ) : (
                    <IconButton
                      size="small"
                      onClick={() => handlePropertyChange('backgroundColor', 'rgba(255, 255, 255, 0.95)')}
                      sx={{
                        backgroundColor: '#F5F5F5',
                        borderRadius: '6px',
                        width: '32px',
                        height: '32px',
                        '&:hover': { backgroundColor: '#E0E0E0' },
                      }}
                    >
                      <RiAddLine size={16} color="#000000" />
                    </IconButton>
                  )}
                </Box>

                {properties.backgroundColor && (
                  <Box
                    sx={{
                      backgroundColor: '#F5F5F5',
                      borderRadius: '8px',
                      padding: '8px',
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '6px',
                        backgroundColor: properties.backgroundColor || 'rgba(255, 255, 255, 0.95)',
                        border: '1px solid #E0E0E0',
                        cursor: 'pointer',
                      }}
                      onClick={() => setFillColorModalOpen(true)}
                    />
                    <TextField
                      type="text"
                      value={properties.backgroundColor.replace(/rgba?\(|\)/g, '').replace(/\s/g, '') || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.includes(',')) {
                          handlePropertyChange('backgroundColor', `rgba(${value})`);
                        } else if (value.startsWith('#')) {
                          handlePropertyChange('backgroundColor', value);
                        } else {
                          handlePropertyChange('backgroundColor', value);
                        }
                      }}
                      size="small"
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'transparent',
                          borderRadius: '6px',
                          '& fieldset': { border: 'none' },
                          '& input': { padding: '4px 8px', fontSize: '14px' },
                        },
                      }}
                    />
                    <TextField
                      type="number"
                      value={Math.round((() => {
                        const bg = properties.backgroundColor || 'rgba(255, 255, 255, 0.95)';
                        const match = bg.match(/rgba?\([^)]+\)/);
                        if (match) {
                          const parts = match[0].match(/[\d.]+/g);
                          return parts && parts.length >= 4 ? parseFloat(parts[3]) * 100 : 100;
                        }
                        return 100;
                      })())}
                      onChange={(e) => {
                        const opacity = Math.max(0, Math.min(100, parseInt(e.target.value) || 100)) / 100;
                        const bg = properties.backgroundColor || 'rgba(255, 255, 255, 0.95)';
                        const match = bg.match(/rgba?\(([^)]+)\)/);
                        if (match) {
                          const parts = match[1].split(',').map(p => p.trim());
                          if (parts.length >= 3) {
                            handlePropertyChange('backgroundColor', `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${opacity})`);
                          }
                        }
                      }}
                      size="small"
                      sx={{
                        width: '70px',
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'transparent',
                          borderRadius: '6px',
                          '& fieldset': { border: 'none' },
                          '& input': { padding: '4px 4px', fontSize: '14px', textAlign: 'right' },
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <Typography sx={{ fontSize: '14px', color: '#999', width: 10 }}>
                            %
                          </Typography>
                        ),
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => {
                        handlePropertyChange('backgroundColor', 'rgba(255, 255, 255, 0.95)');
                      }}
                      sx={{
                        padding: '4px',
                        color: '#666666',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                      }}
                    >
                      <RiEyeLine size={20} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        handlePropertyChange('backgroundColor', null);
                      }}
                      sx={{
                        padding: '4px',
                        color: '#666666',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                      }}
                    >
                      <RiSubtractLine size={20} />
                    </IconButton>
                  </Box>
                )}
              </Box>

              {/* Separator */}
              <Separator withAccent={false} />

              {/* Stroke Color Section */}
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    fontSize: '14px',
                    color: '#000000',
                    marginBottom: '12px',
                  }}
                >
                  Stroke Color
                </Typography>

                {/* Stroke Color Row */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    marginBottom: '12px',
                  }}
                >
                  {/* Color Picker */}
                  <Box
                    sx={{
                      backgroundColor: '#F5F5F5',
                      borderRadius: '8px',
                      padding: '4px',
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '6px',
                        backgroundColor: properties.strokeColor || '#78CE75',
                        border: '1px solid #E0E0E0',
                        cursor: 'pointer',
                        flexShrink: 0,
                      }}
                      onClick={() => setStrokeColorModalOpen(true)}
                    />
                    <TextField
                      type="text"
                      value={(properties.strokeColor || '#78CE75').replace('#', '').toUpperCase()}
                      onChange={(e) => {
                        const value = e.target.value.replace('#', '');
                        if (value.length === 6) {
                          handlePropertyChange('strokeColor', `#${value}`);
                        }
                      }}
                      size="small"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'transparent',
                          borderRadius: '6px',
                          '& fieldset': { border: 'none' },
                          '& input': {
                            padding: '4px 4px',
                            fontSize: '14px',
                            textTransform: 'uppercase',
                            color: '#000000',
                          },
                        },
                      }}
                    />
                  </Box>

                  {/* Opacity */}
                  <Box
                    sx={{
                      backgroundColor: '#F5F5F5',
                      borderRadius: '8px',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      maxWidth: '100px',
                    }}
                  >
                    <TextField
                      type="number"
                      value={Math.round((properties.strokeOpacity !== undefined ? properties.strokeOpacity : 1) * 100)}
                      onChange={(e) => handlePropertyChange('strokeOpacity', Math.max(0, Math.min(100, parseInt(e.target.value) || 100)) / 100)}
                      size="small"
                      sx={{
                        width: '70px',
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'transparent',
                          borderRadius: '6px',
                          '& fieldset': { border: 'none' },
                          '& input': {
                            padding: '4px 4px',
                            fontSize: '14px',
                            textAlign: 'right',
                            color: '#000000',
                          },
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <Typography sx={{ fontSize: '14px', color: '#999', width: 10, maxWidth: 10, textAlign: 'right' }}>
                            %
                          </Typography>
                        ),
                      }}
                    />
                  </Box>

                  {/* Visibility Toggle */}
                  <IconButton
                    size="small"
                    onClick={() => {
                      handlePropertyChange('strokeVisible', properties.strokeVisible !== false ? false : true);
                    }}
                    sx={{
                      padding: '4px',
                      color: '#666666',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                    }}
                  >
                    {properties.strokeVisible !== false ? (
                      <RiEyeLine size={20} />
                    ) : (
                      <RiEyeOffLine size={20} />
                    )}
                  </IconButton>

                  {/* Remove/Minus Icon */}
                  <IconButton
                    size="small"
                    onClick={() => {
                      handlePropertyChange('strokeColor', null);
                    }}
                    sx={{
                      padding: '4px',
                      color: '#666666',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                    }}
                  >
                    <RiSubtractLine size={20} />
                  </IconButton>
                </Box>
                {/* Stroke Width and Position */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                  }}
                >
                  <TextField
                    type="number"
                    value={properties.strokeWidth || 0}
                    onChange={(e) => handlePropertyChange('strokeWidth', Math.max(0, parseInt(e.target.value) || 0))}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ marginRight: '8px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                          <Box sx={{ width: '2px', height: '8px', backgroundColor: '#666666' }} />
                          <Box sx={{ width: '3px', height: '10px', backgroundColor: '#666666' }} />
                          <Box sx={{ width: '4px', height: '12px', backgroundColor: '#666666' }} />
                        </Box>
                      ),
                    }}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#F5F5F5',
                        borderRadius: '8px',
                        '& fieldset': { border: 'none' },
                        '& input': { padding: '8px 12px', fontSize: '14px' },
                      },
                    }}
                  />
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <Select
                      value={properties.strokePosition || 'inside'}
                      onChange={(e) => handlePropertyChange('strokePosition', e.target.value)}
                      sx={{
                        backgroundColor: '#F5F5F5',
                        borderRadius: '8px',
                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                      }}
                    >
                      <MenuItem value="inside">Inside</MenuItem>
                      <MenuItem value="outside">Outside</MenuItem>
                      <MenuItem value="center">Center</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Background Color Modal */}
              <ColorPickerModal
                open={fillColorModalOpen}
                onClose={() => setFillColorModalOpen(false)}
                currentColor={properties.backgroundColor || 'rgba(255, 255, 255, 0.95)'}
                onColorSelect={(color) => {
                  handlePropertyChange('backgroundColor', color);
                }}
                title="Background Color"
              />

              {/* Stroke Color Modal */}
              <ColorPickerModal
                open={strokeColorModalOpen}
                onClose={() => setStrokeColorModalOpen(false)}
                currentColor={properties.strokeColor || '#78CE75'}
                onColorSelect={(color) => {
                  handlePropertyChange('strokeColor', color);
                }}
                title="Stroke Color"
              />

              {/* Box Shadow Color Modal */}
              <ColorPickerModal
                open={boxShadowColorModalOpen}
                onClose={() => setBoxShadowColorModalOpen(false)}
                currentColor={properties.boxShadowColor || 'rgba(0,0,0,0.25)'}
                onColorSelect={(color) => {
                  handlePropertyChange('boxShadowColor', color);
                }}
                title="Shadow Color"
              />

              {/* Box Shadow Section */}
              <Separator />
              <Box sx={{ marginBottom: '24px' }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    fontSize: '14px',
                    color: '#000000',
                    marginBottom: '12px',
                  }}
                >
                  Box Shadow
                </Typography>

                {/* Shadow Visibility Toggle */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px',
                    padding: '8px',
                    backgroundColor: '#F5F5F5',
                    borderRadius: '8px',
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: '12px', color: '#666' }}>
                    Enable Shadow
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => handlePropertyChange('boxShadowVisible', !(properties.boxShadowVisible !== false))}
                    sx={{
                      backgroundColor: properties.boxShadowVisible !== false ? '#6B22D5' : '#F5F5F5',
                      borderRadius: '6px',
                      minWidth: 'auto',
                      padding: '4px 12px',
                      '&:hover': {
                        backgroundColor: properties.boxShadowVisible !== false ? '#5B1DB7' : '#E0E0E0',
                      },
                    }}
                  >
                    {properties.boxShadowVisible !== false ? (
                      <RiEyeLine size={20} color="#FFFFFF" />
                    ) : (
                      <RiEyeOffLine size={20} color="#666" />
                    )}
                  </Button>
                </Box>

                {properties.boxShadowVisible !== false && (
                  <>
                    {/* Shadow Color */}
                    <Box sx={{ marginBottom: '12px' }}>
                      <Typography variant="caption" sx={{ display: 'block', marginBottom: '8px', color: '#666', fontSize: '12px' }}>
                        Shadow Color
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: '8px',
                          alignItems: 'center',
                        }}
                      >
                        <Box
                          sx={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '6px',
                            backgroundColor: properties.boxShadowColor || 'rgba(0,0,0,0.25)',
                            border: '1px solid #E0E0E0',
                            cursor: 'pointer',
                          }}
                          onClick={() => setBoxShadowColorModalOpen(true)}
                        />
                        <TextField
                          size="small"
                          placeholder="rgba(0,0,0,0.25)"
                          value={properties.boxShadowColor || 'rgba(0,0,0,0.25)'}
                          onChange={(e) => handlePropertyChange('boxShadowColor', e.target.value)}
                          sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#F5F5F5',
                              borderRadius: '6px',
                              '& fieldset': { border: 'none' },
                            },
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Shadow Offset X */}
                    <Box sx={{ marginBottom: '12px' }}>
                      <Typography variant="caption" sx={{ display: 'block', marginBottom: '8px', color: '#666', fontSize: '12px' }}>
                        Offset X
                      </Typography>
                      <TextField
                        size="small"
                        type="number"
                        value={properties.boxShadowOffsetX || 0}
                        onChange={(e) => handlePropertyChange('boxShadowOffsetX', parseInt(e.target.value) || 0)}
                        sx={{
                          width: '100%',
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#F5F5F5',
                            borderRadius: '6px',
                            '& fieldset': { border: 'none' },
                          },
                        }}
                      />
                    </Box>

                    {/* Shadow Offset Y */}
                    <Box sx={{ marginBottom: '12px' }}>
                      <Typography variant="caption" sx={{ display: 'block', marginBottom: '8px', color: '#666', fontSize: '12px' }}>
                        Offset Y
                      </Typography>
                      <TextField
                        size="small"
                        type="number"
                        value={properties.boxShadowOffsetY || 0}
                        onChange={(e) => handlePropertyChange('boxShadowOffsetY', parseInt(e.target.value) || 0)}
                        sx={{
                          width: '100%',
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#F5F5F5',
                            borderRadius: '6px',
                            '& fieldset': { border: 'none' },
                          },
                        }}
                      />
                    </Box>

                    {/* Shadow Blur / Radius */}
                    <Box sx={{ marginBottom: '12px' }}>
                      <Typography variant="caption" sx={{ display: 'block', marginBottom: '8px', color: '#666', fontSize: '12px' }}>
                        Shadow Radius (Blur)
                      </Typography>
                      <TextField
                        size="small"
                        type="number"
                        value={properties.boxShadowBlur || 0}
                        onChange={(e) => handlePropertyChange('boxShadowBlur', Math.max(0, parseInt(e.target.value) || 0))}
                        placeholder="0"
                        sx={{
                          width: '100%',
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#F5F5F5',
                            borderRadius: '6px',
                            '& fieldset': { border: 'none' },
                          },
                        }}
                      />
                    </Box>

                    {/* Shadow Spread */}
                    <Box sx={{ marginBottom: '12px' }}>
                      <Typography variant="caption" sx={{ display: 'block', marginBottom: '8px', color: '#666', fontSize: '12px' }}>
                        Spread
                      </Typography>
                      <TextField
                        size="small"
                        type="number"
                        value={properties.boxShadowSpread || 0}
                        onChange={(e) => handlePropertyChange('boxShadowSpread', parseInt(e.target.value) || 0)}
                        sx={{
                          width: '100%',
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#F5F5F5',
                            borderRadius: '6px',
                            '& fieldset': { border: 'none' },
                          },
                        }}
                      />
                    </Box>
                  </>
                )}
              </Box>
            </>
          )}

          {menuItemTab === 1 && (
            <>

              {/* Typography Section */}
              <Box sx={{ marginBottom: '24px' }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    fontSize: '14px',
                    color: '#000000',
                    marginBottom: '12px',
                  }}
                >
                  Typography
                </Typography>

                {/* Font Family */}
                <Autocomplete
                  size="small"
                  options={FONT_FAMILIES}
                  value={properties.nameFontFamily || 'Outfit'}
                  onChange={(event, newValue) => {
                    handlePropertyChange('nameFontFamily', newValue || 'Outfit');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search font..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#F5F5F5',
                          borderRadius: '8px',
                          '& fieldset': {
                            border: '1px solid #E0E0E0',
                          },
                          '&:hover fieldset': {
                            border: '1px solid #E0E0E0',
                          },
                          '&.Mui-focused fieldset': {
                            border: '1px solid #E0E0E0',
                          },
                        },
                      }}
                    />
                  )}
                  sx={{ marginBottom: '12px' }}
                  disableClearable
                />

                {/* Font Weight and Font Size Row */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    marginBottom: '12px',
                  }}
                >
                  {/* Font Weight */}
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <Select
                      value={(() => {
                        const weight = properties.nameFontWeight || 400;
                        const weightLabels = {
                          100: 'thin',
                          200: 'extraLight',
                          300: 'light',
                          400: 'regular',
                          500: 'medium',
                          600: 'semiBold',
                          700: 'bold',
                          800: 'extraBold',
                          900: 'black',
                        };
                        return weightLabels[weight] || 'regular';
                      })()}
                      onChange={(e) => {
                        const weightMap = {
                          thin: 100,
                          extraLight: 200,
                          light: 300,
                          regular: 400,
                          medium: 500,
                          semiBold: 600,
                          bold: 700,
                          extraBold: 800,
                          black: 900,
                        };
                        handlePropertyChange('nameFontWeight', weightMap[e.target.value] || 400);
                      }}
                      sx={{
                        backgroundColor: '#F5F5F5',
                        borderRadius: '8px',
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: '1px solid #E0E0E0',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          border: '1px solid #E0E0E0',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          border: '1px solid #E0E0E0',
                        },
                      }}
                    >
                      <MenuItem value="thin">Thin</MenuItem>
                      <MenuItem value="extraLight">Extra Light</MenuItem>
                      <MenuItem value="light">Light</MenuItem>
                      <MenuItem value="regular">Regular</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="semiBold">Semi Bold</MenuItem>
                      <MenuItem value="bold">Bold</MenuItem>
                      <MenuItem value="extraBold">Extra Bold</MenuItem>
                      <MenuItem value="black">Black</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Font Size */}
                  <Box sx={{ position: 'relative', flex: 1 }}>
                    <TextField
                      type="number"
                      value={parseInt(properties.nameFontSize) || 24}
                      onChange={(e) => handlePropertyChange('nameFontSize', `${e.target.value}px`)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#F5F5F5',
                          borderRadius: '8px',
                          '& fieldset': {
                            border: '1px solid #E0E0E0',
                          },
                        },
                      }}
                      fullWidth
                    />
                  </Box>
                </Box>

                {/* Text Alignment Buttons */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    alignItems: 'center',
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => handlePropertyChange('nameTextAlign', 'left')}
                    sx={{
                      backgroundColor: (properties.nameTextAlign || 'left') === 'left' ? '#6B22D5' : '#F5F5F5',
                      borderRadius: '6px',
                      width: '32px',
                      height: '32px',
                      '&:hover': { backgroundColor: (properties.nameTextAlign || 'left') === 'left' ? '#5B1DB7' : '#E0E0E0' },
                    }}
                  >
                    <RiAlignLeft size={20} color={(properties.nameTextAlign || 'left') === 'left' ? '#FFFFFF' : '#000000'} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handlePropertyChange('nameTextAlign', 'center')}
                    sx={{
                      backgroundColor: properties.nameTextAlign === 'center' ? '#6B22D5' : '#F5F5F5',
                      borderRadius: '6px',
                      width: '32px',
                      height: '32px',
                      '&:hover': { backgroundColor: properties.nameTextAlign === 'center' ? '#5B1DB7' : '#E0E0E0' },
                    }}
                  >
                    <RiAlignCenter size={20} color={properties.nameTextAlign === 'center' ? '#FFFFFF' : '#000000'} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handlePropertyChange('nameTextAlign', 'right')}
                    sx={{
                      backgroundColor: properties.nameTextAlign === 'right' ? '#6B22D5' : '#F5F5F5',
                      borderRadius: '6px',
                      width: '32px',
                      height: '32px',
                      '&:hover': { backgroundColor: properties.nameTextAlign === 'right' ? '#5B1DB7' : '#E0E0E0' },
                    }}
                  >
                    <RiAlignRight size={20} color={properties.nameTextAlign === 'right' ? '#FFFFFF' : '#000000'} />
                  </IconButton>
                </Box>
              </Box>

              {/* Separator */}
              <Separator withAccent={false} />

              {/* Item Color Section */}
              <Box sx={{ marginBottom: '24px' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px',
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      fontSize: '14px',
                      color: '#000000',
                    }}
                  >
                    Item Color
                  </Typography>

                </Box>
                <Box
                  sx={{
                    backgroundColor: '#F5F5F5',
                    borderRadius: '8px',
                    padding: '8px',
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    marginBottom: '12px',
                  }}
                >
                  <Box
                    sx={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '4px',
                      backgroundColor: properties.nameColor || '#111827',
                      border: '1px solid #E0E0E0',
                      cursor: 'pointer',
                    }}
                    onClick={() => setTextColorModalOpen(true)}
                  />
                  <TextField
                    type="text"
                    value={(properties.nameColor || '#111827').replace('#', '').toUpperCase()}
                    onChange={(e) => {
                      const value = e.target.value.replace('#', '');
                      if (value.length === 6) {
                        handlePropertyChange('nameColor', `#${value}`);
                      }
                    }}
                    size="small"
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'transparent',
                        borderRadius: '6px',
                        '& fieldset': { border: 'none' },
                        '& input': { padding: '4px 8px', fontSize: '14px', textTransform: 'uppercase' },
                      },
                    }}
                  />
                  <TextField
                    type="number"
                    value={Math.round((properties.nameColorOpacity !== undefined ? properties.nameColorOpacity : 1) * 100)}
                    onChange={(e) => {
                      handlePropertyChange('nameColorOpacity', Math.max(0, Math.min(100, parseInt(e.target.value) || 100)) / 100);
                    }}
                    size="small"
                    sx={{
                      width: '70px',
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'transparent',
                        borderRadius: '6px',
                        '& fieldset': { border: 'none' },
                        '& input': { padding: '4px 4px', fontSize: '14px', textAlign: 'right' },
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <Typography sx={{ fontSize: '14px', color: '#999', width: 10 }}>
                          %
                        </Typography>
                      ),
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      handlePropertyChange('nameColorVisible', properties.nameColorVisible !== false ? false : true);
                    }}
                    sx={{
                      padding: '4px',
                      color: '#666666',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                    }}
                  >
                    {properties.nameColorVisible !== false ? (
                      <RiEyeLine size={20} />
                    ) : (
                      <RiEyeOffLine size={20} />
                    )}
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      handlePropertyChange('nameColor', null);
                    }}
                    sx={{
                      padding: '4px',
                      color: '#666666',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                    }}
                  >
                    <RiSubtractLine size={20} />
                  </IconButton>
                </Box>
              </Box>

              {/* Separator */}
              <Separator withAccent={false} />
              
              <Box sx={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => setMenuItemsModalOpen(true)}
                  sx={{
                    borderRadius: '6px',
                    width: '32px',
                    height: '32px',
                  }}
                >
                  <RiListCheck2 size={24} color={'#000000'} />
                </IconButton>
                <Typography
                  variant=""
                  sx={{
                    fontWeight: 600,
                    fontSize: '18px',
                    color: '#000000',
                  }}
                >
                  Items
                </Typography>
              </Box>

              {/* Item Color Modal */}
              <ColorPickerModal
                open={textColorModalOpen}
                onClose={() => setTextColorModalOpen(false)}
                currentColor={properties.nameColor || '#111827'}
                onColorSelect={(color) => {
                  handlePropertyChange('nameColor', color);
                }}
                title="Item Color"
              />
            </>
          )}

          {menuItemTab === 2 && (
            <>
              {/* Typography Section */}
              <Box sx={{ marginBottom: '24px' }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    fontSize: '14px',
                    color: '#000000',
                    marginBottom: '12px',
                  }}
                >
                  Typography
                </Typography>

                {/* Font Family */}
                <Autocomplete
                  size="small"
                  options={FONT_FAMILIES}
                  value={properties.priceFontFamily || 'Outfit'}
                  onChange={(event, newValue) => {
                    handlePropertyChange('priceFontFamily', newValue || 'Outfit');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search font..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#F5F5F5',
                          borderRadius: '8px',
                          '& fieldset': {
                            border: '1px solid #E0E0E0',
                          },
                          '&:hover fieldset': {
                            border: '1px solid #E0E0E0',
                          },
                          '&.Mui-focused fieldset': {
                            border: '1px solid #E0E0E0',
                          },
                        },
                      }}
                    />
                  )}
                  sx={{ marginBottom: '12px' }}
                  disableClearable
                />

                {/* Font Weight and Font Size Row */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    marginBottom: '12px',
                  }}
                >
                  {/* Font Weight */}
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <Select
                      value={(() => {
                        const weight = properties.priceFontWeight || 600;
                        const weightLabels = {
                          100: 'thin',
                          200: 'extraLight',
                          300: 'light',
                          400: 'regular',
                          500: 'medium',
                          600: 'semiBold',
                          700: 'bold',
                          800: 'extraBold',
                          900: 'black',
                        };
                        return weightLabels[weight] || 'semiBold';
                      })()}
                      onChange={(e) => {
                        const weightMap = {
                          thin: 100,
                          extraLight: 200,
                          light: 300,
                          regular: 400,
                          medium: 500,
                          semiBold: 600,
                          bold: 700,
                          extraBold: 800,
                          black: 900,
                        };
                        handlePropertyChange('priceFontWeight', weightMap[e.target.value] || 600);
                      }}
                      sx={{
                        backgroundColor: '#F5F5F5',
                        borderRadius: '8px',
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: '1px solid #E0E0E0',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          border: '1px solid #E0E0E0',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          border: '1px solid #E0E0E0',
                        },
                      }}
                    >
                      <MenuItem value="thin">Thin</MenuItem>
                      <MenuItem value="extraLight">Extra Light</MenuItem>
                      <MenuItem value="light">Light</MenuItem>
                      <MenuItem value="regular">Regular</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="semiBold">Semi Bold</MenuItem>
                      <MenuItem value="bold">Bold</MenuItem>
                      <MenuItem value="extraBold">Extra Bold</MenuItem>
                      <MenuItem value="black">Black</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Font Size */}
                  <Box sx={{ position: 'relative', flex: 1 }}>
                    <TextField
                      type="number"
                      value={parseInt(properties.priceFontSize) || 20}
                      onChange={(e) => handlePropertyChange('priceFontSize', `${e.target.value}px`)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#F5F5F5',
                          borderRadius: '8px',
                          '& fieldset': {
                            border: '1px solid #E0E0E0',
                          },
                        },
                      }}
                      fullWidth
                    />
                  </Box>
                </Box>

                {/* Text Alignment Buttons */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    alignItems: 'center',
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => handlePropertyChange('priceTextAlign', 'left')}
                    sx={{
                      backgroundColor: (properties.priceTextAlign || 'right') === 'left' ? '#6B22D5' : '#F5F5F5',
                      borderRadius: '6px',
                      width: '32px',
                      height: '32px',
                      '&:hover': { backgroundColor: (properties.priceTextAlign || 'right') === 'left' ? '#5B1DB7' : '#E0E0E0' },
                    }}
                  >
                    <RiAlignLeft size={20} color={(properties.priceTextAlign || 'right') === 'left' ? '#FFFFFF' : '#000000'} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handlePropertyChange('priceTextAlign', 'center')}
                    sx={{
                      backgroundColor: properties.priceTextAlign === 'center' ? '#6B22D5' : '#F5F5F5',
                      borderRadius: '6px',
                      width: '32px',
                      height: '32px',
                      '&:hover': { backgroundColor: properties.priceTextAlign === 'center' ? '#5B1DB7' : '#E0E0E0' },
                    }}
                  >
                    <RiAlignCenter size={20} color={properties.priceTextAlign === 'center' ? '#FFFFFF' : '#000000'} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handlePropertyChange('priceTextAlign', 'right')}
                    sx={{
                      backgroundColor: (properties.priceTextAlign || 'right') === 'right' ? '#6B22D5' : '#F5F5F5',
                      borderRadius: '6px',
                      width: '32px',
                      height: '32px',
                      '&:hover': { backgroundColor: (properties.priceTextAlign || 'right') === 'right' ? '#5B1DB7' : '#E0E0E0' },
                    }}
                  >
                    <RiAlignRight size={20} color={(properties.priceTextAlign || 'right') === 'right' ? '#FFFFFF' : '#000000'} />
                  </IconButton>
                </Box>
              </Box>

              {/* Separator */}
              <Separator withAccent={false} />

              {/* Price Color Section */}
              <Box sx={{ marginBottom: '24px' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px',
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      fontSize: '14px',
                      color: '#000000',
                    }}
                  >
                    Price Color
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: '#F5F5F5',
                    borderRadius: '8px',
                    padding: '8px',
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    marginBottom: '12px',
                  }}
                >
                  <Box
                    sx={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '4px',
                      backgroundColor: properties.priceColor || '#C61F2F',
                      border: '1px solid #E0E0E0',
                      cursor: 'pointer',
                    }}
                    onClick={() => setMenuItemPriceColorModalOpen(true)}
                  />
                  <TextField
                    type="text"
                    value={(properties.priceColor || '#C61F2F').replace('#', '').toUpperCase()}
                    onChange={(e) => {
                      const value = e.target.value.replace('#', '');
                      if (value.length === 6) {
                        handlePropertyChange('priceColor', `#${value}`);
                      }
                    }}
                    size="small"
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'transparent',
                        borderRadius: '6px',
                        '& fieldset': { border: 'none' },
                        '& input': { padding: '4px 8px', fontSize: '14px', textTransform: 'uppercase' },
                      },
                    }}
                  />
                  <TextField
                    type="number"
                    value={Math.round((properties.priceColorOpacity !== undefined ? properties.priceColorOpacity : 1) * 100)}
                    onChange={(e) => {
                      handlePropertyChange('priceColorOpacity', Math.max(0, Math.min(100, parseInt(e.target.value) || 100)) / 100);
                    }}
                    size="small"
                    sx={{
                      width: '70px',
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'transparent',
                        borderRadius: '6px',
                        '& fieldset': { border: 'none' },
                        '& input': { padding: '4px 4px', fontSize: '14px', textAlign: 'right' },
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <Typography sx={{ fontSize: '14px', color: '#999', width: 10 }}>
                          %
                        </Typography>
                      ),
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      handlePropertyChange('priceColorVisible', properties.priceColorVisible !== false ? false : true);
                    }}
                    sx={{
                      padding: '4px',
                      color: '#666666',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                    }}
                  >
                    {properties.priceColorVisible !== false ? (
                      <RiEyeLine size={20} />
                    ) : (
                      <RiEyeOffLine size={20} />
                    )}
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      handlePropertyChange('priceColor', null);
                    }}
                    sx={{
                      padding: '4px',
                      color: '#666666',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                    }}
                  >
                    <RiSubtractLine size={20} />
                  </IconButton>
                </Box>
              </Box>

              {/* Price Color Modal */}
              <ColorPickerModal
                open={menuItemPriceColorModalOpen}
                onClose={() => setMenuItemPriceColorModalOpen(false)}
                currentColor={properties.priceColor || '#C61F2F'}
                onColorSelect={(color) => {
                  handlePropertyChange('priceColor', color);
                }}
                title="Price Color"
              />
            </>
          )}

          {menuItemTab === 3 && (
            <>
              {/* Typography Section */}
              <Box sx={{ marginBottom: '24px' }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    fontSize: '14px',
                    color: '#000000',
                    marginBottom: '12px',
                  }}
                >
                  Typography
                </Typography>

                {/* Font Family */}
                <Autocomplete
                  size="small"
                  options={FONT_FAMILIES}
                  value={properties.descriptionFontFamily || 'Outfit'}
                  onChange={(event, newValue) => {
                    handlePropertyChange('descriptionFontFamily', newValue || 'Outfit');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search font..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#F5F5F5',
                          borderRadius: '8px',
                          '& fieldset': {
                            border: '1px solid #E0E0E0',
                          },
                          '&:hover fieldset': {
                            border: '1px solid #E0E0E0',
                          },
                          '&.Mui-focused fieldset': {
                            border: '1px solid #E0E0E0',
                          },
                        },
                      }}
                    />
                  )}
                  sx={{ marginBottom: '12px' }}
                  disableClearable
                />

                {/* Font Weight and Font Size Row */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    marginBottom: '12px',
                  }}
                >
                  {/* Font Weight */}
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <Select
                      value={(() => {
                        const weight = properties.descriptionFontWeight || 400;
                        const weightLabels = {
                          100: 'thin',
                          200: 'extraLight',
                          300: 'light',
                          400: 'regular',
                          500: 'medium',
                          600: 'semiBold',
                          700: 'bold',
                          800: 'extraBold',
                          900: 'black',
                        };
                        return weightLabels[weight] || 'regular';
                      })()}
                      onChange={(e) => {
                        const weightMap = {
                          thin: 100,
                          extraLight: 200,
                          light: 300,
                          regular: 400,
                          medium: 500,
                          semiBold: 600,
                          bold: 700,
                          extraBold: 800,
                          black: 900,
                        };
                        handlePropertyChange('descriptionFontWeight', weightMap[e.target.value] || 400);
                      }}
                      sx={{
                        backgroundColor: '#F5F5F5',
                        borderRadius: '8px',
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: '1px solid #E0E0E0',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          border: '1px solid #E0E0E0',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          border: '1px solid #E0E0E0',
                        },
                      }}
                    >
                      <MenuItem value="thin">Thin</MenuItem>
                      <MenuItem value="extraLight">Extra Light</MenuItem>
                      <MenuItem value="light">Light</MenuItem>
                      <MenuItem value="regular">Regular</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="semiBold">Semi Bold</MenuItem>
                      <MenuItem value="bold">Bold</MenuItem>
                      <MenuItem value="extraBold">Extra Bold</MenuItem>
                      <MenuItem value="black">Black</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Font Size */}
                  <Box sx={{ position: 'relative', flex: 1 }}>
                    <TextField
                      type="number"
                      value={parseInt(properties.descriptionFontSize) || 16}
                      onChange={(e) => handlePropertyChange('descriptionFontSize', `${e.target.value}px`)}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#F5F5F5',
                          borderRadius: '8px',
                          '& fieldset': {
                            border: '1px solid #E0E0E0',
                          },
                        },
                      }}
                      fullWidth
                    />
                  </Box>
                </Box>

                {/* Text Alignment Buttons */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    alignItems: 'center',
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => handlePropertyChange('descriptionTextAlign', 'left')}
                    sx={{
                      backgroundColor: (properties.descriptionTextAlign || 'left') === 'left' ? '#6B22D5' : '#F5F5F5',
                      borderRadius: '6px',
                      width: '32px',
                      height: '32px',
                      '&:hover': { backgroundColor: (properties.descriptionTextAlign || 'left') === 'left' ? '#5B1DB7' : '#E0E0E0' },
                    }}
                  >
                    <RiAlignLeft size={20} color={(properties.descriptionTextAlign || 'left') === 'left' ? '#FFFFFF' : '#000000'} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handlePropertyChange('descriptionTextAlign', 'center')}
                    sx={{
                      backgroundColor: properties.descriptionTextAlign === 'center' ? '#6B22D5' : '#F5F5F5',
                      borderRadius: '6px',
                      width: '32px',
                      height: '32px',
                      '&:hover': { backgroundColor: properties.descriptionTextAlign === 'center' ? '#5B1DB7' : '#E0E0E0' },
                    }}
                  >
                    <RiAlignCenter size={20} color={properties.descriptionTextAlign === 'center' ? '#FFFFFF' : '#000000'} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handlePropertyChange('descriptionTextAlign', 'right')}
                    sx={{
                      backgroundColor: properties.descriptionTextAlign === 'right' ? '#6B22D5' : '#F5F5F5',
                      borderRadius: '6px',
                      width: '32px',
                      height: '32px',
                      '&:hover': { backgroundColor: properties.descriptionTextAlign === 'right' ? '#5B1DB7' : '#E0E0E0' },
                    }}
                  >
                    <RiAlignRight size={20} color={properties.descriptionTextAlign === 'right' ? '#FFFFFF' : '#000000'} />
                  </IconButton>
                </Box>
              </Box>

              {/* Separator */}
              <Separator withAccent={false} />

              {/* Description Color Section */}
              <Box sx={{ marginBottom: '24px' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px',
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      fontSize: '14px',
                      color: '#000000',
                    }}
                  >
                    Description Color
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: '#F5F5F5',
                    borderRadius: '8px',
                    padding: '8px',
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    marginBottom: '12px',
                  }}
                >
                  <Box
                    sx={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '4px',
                      backgroundColor: properties.descriptionColor || '#6B7280',
                      border: '1px solid #E0E0E0',
                      cursor: 'pointer',
                    }}
                    onClick={() => setMenuItemDescriptionColorModalOpen(true)}
                  />
                  <TextField
                    type="text"
                    value={(properties.descriptionColor || '#6B7280').replace('#', '').toUpperCase()}
                    onChange={(e) => {
                      const value = e.target.value.replace('#', '');
                      if (value.length === 6) {
                        handlePropertyChange('descriptionColor', `#${value}`);
                      }
                    }}
                    size="small"
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'transparent',
                        borderRadius: '6px',
                        '& fieldset': { border: 'none' },
                        '& input': { padding: '4px 8px', fontSize: '14px', textTransform: 'uppercase' },
                      },
                    }}
                  />
                  <TextField
                    type="number"
                    value={Math.round((properties.descriptionColorOpacity !== undefined ? properties.descriptionColorOpacity : 1) * 100)}
                    onChange={(e) => {
                      handlePropertyChange('descriptionColorOpacity', Math.max(0, Math.min(100, parseInt(e.target.value) || 100)) / 100);
                    }}
                    size="small"
                    sx={{
                      width: '70px',
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'transparent',
                        borderRadius: '6px',
                        '& fieldset': { border: 'none' },
                        '& input': { padding: '4px 4px', fontSize: '14px', textAlign: 'right' },
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <Typography sx={{ fontSize: '14px', color: '#999', width: 10 }}>
                          %
                        </Typography>
                      ),
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      handlePropertyChange('descriptionColorVisible', properties.descriptionColorVisible !== false ? false : true);
                    }}
                    sx={{
                      padding: '4px',
                      color: '#666666',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                    }}
                  >
                    {properties.descriptionColorVisible !== false ? (
                      <RiEyeLine size={20} />
                    ) : (
                      <RiEyeOffLine size={20} />
                    )}
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      handlePropertyChange('descriptionColor', null);
                    }}
                    sx={{
                      padding: '4px',
                      color: '#666666',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                    }}
                  >
                    <RiSubtractLine size={20} />
                  </IconButton>
                </Box>
              </Box>

              {/* Description Color Modal */}
              <ColorPickerModal
                open={menuItemDescriptionColorModalOpen}
                onClose={() => setMenuItemDescriptionColorModalOpen(false)}
                currentColor={properties.descriptionColor || '#6B7280'}
                onColorSelect={(color) => {
                  handlePropertyChange('descriptionColor', color);
                }}
                title="Description Color"
              />
            </>
          )}
        </Box>
      )}

      {/* Menu Items Modal */}
      {selectedElement?.type === 'menuItem' && (
        <MenuItemsModal
          open={menuItemsModalOpen}
          onClose={() => setMenuItemsModalOpen(false)}
          items={properties.items || []}
          subcategories={properties.subcategories || []}
          sizes={properties.sizes || []}
          layout={properties.layout || 'listed'}
          sections={properties.sections || []}
          currentElementProperties={properties}
          currentElementSize={size}
          currentElementPosition={position}
          onItemsChange={(updatedItems) => {
            handlePropertyChange('items', updatedItems);
          }}
          onSubcategoriesChange={(updatedSubcategories) => {
            handlePropertyChange('subcategories', updatedSubcategories);
          }}
          onSizesChange={(updatedSizes) => {
            handlePropertyChange('sizes', updatedSizes);
          }}
          onSectionsChange={(updatedSections) => {
            handlePropertyChange('sections', updatedSections);
          }}
        />
      )}

      {/* Clipping Mask Section - Available for all element types except shapes used as masks */}
      {selectedElement && selectedElement.type !== 'shape' && (
        <>
          <Separator withAccent={false} />
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: '#333',
                mb: 1.5,
                fontSize: '13px',
              }}
            >
              Clipping Mask
            </Typography>
            
            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
              <InputLabel id="clip-mask-select-label">Use Element as Mask</InputLabel>
              <Select
                labelId="clip-mask-select-label"
                label="Use Element as Mask"
                value={properties?.clipMaskId || ''}
                onChange={(e) => {
                  handlePropertyChange('clipMaskId', e.target.value || null);
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'transparent',
                    borderRadius: '6px',
                    '& fieldset': {
                      borderColor: '#E0E0E0',
                    },
                  },
                }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {elements
                  .filter(el => {
                    if (el.id === selectedElement.id) return false;
                    // Menu items, shapes, and images can always be used as masks
                    if (el.type === 'menuItem' || el.type === 'shape' || el.type === 'image') {
                      return true;
                    }
                    // Text elements can be used if marked as mask
                    if (el.type === 'text') {
                      return el.properties?.isClipMask || false;
                    }
                    return false;
                  })
                  .map((el) => (
                    <MenuItem key={el.id} value={el.id}>
                      {el.type === 'shape' 
                        ? `${el.properties?.shapeType || 'Rectangle'} Shape`
                        : el.type === 'image'
                        ? 'Image'
                        : el.type === 'menuItem'
                        ? 'Menu Item'
                        : 'Text'} - {el.id.substring(0, 8)}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            
            <Typography
              variant="caption"
              sx={{
                color: '#666',
                fontSize: '11px',
                display: 'block',
                mt: 0.5,
              }}
            >
              Select an element to use as a clipping mask. The selected element will be clipped to the shape of the mask element.
            </Typography>
          </Box>
        </>
      )}

      {/* Mark as Clipping Mask Section - For shapes, images, and menu items */}
      {selectedElement && (selectedElement.type === 'shape' || selectedElement.type === 'image' || selectedElement.type === 'menuItem') && (
        <>
          <Separator withAccent={false} />
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: '#333',
                mb: 1.5,
                fontSize: '13px',
              }}
            >
              Mask Options
            </Typography>
            
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: '#666',
                  fontSize: '12px',
                }}
              >
                Use as Clipping Mask
              </Typography>
              <Button
                size="small"
                variant={properties?.isClipMask ? 'contained' : 'outlined'}
                onClick={() => {
                  handlePropertyChange('isClipMask', !properties?.isClipMask);
                }}
                sx={{
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.5,
                  fontSize: '11px',
                  textTransform: 'none',
                  borderRadius: '4px',
                }}
              >
                {properties?.isClipMask ? 'Enabled' : 'Disabled'}
              </Button>
            </Box>
            
            <Typography
              variant="caption"
              sx={{
                color: '#666',
                fontSize: '11px',
                display: 'block',
                mt: 0.5,
              }}
            >
              Enable this to allow other elements to use this element as a clipping mask.
            </Typography>
          </Box>
        </>
      )}

      {/* Style Properties for non-shape elements */}
      {/* {type !== 'shape' && (
        <Box>
          <Typography variant="subtitle2" sx={{ marginBottom: '8px' }}>
            Style
          </Typography>
          <TextField
            label="Background Color"
            type="color"
            size="small"
            value={properties.backgroundColor || '#FFFFFF'}
            onChange={(e) => handlePropertyChange('backgroundColor', e.target.value)}
            fullWidth
            sx={{ marginBottom: 2 }}
          />
        </Box>
      )} */}
    </Box>
  );
};

export default PropertiesPanelV2;
